import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  IconButton,
  Alert,
  Paper,
  Divider,
  InputAdornment,
  Tooltip,
  Avatar,
  CircularProgress,
  ClickAwayListener,
  Fade,
  Grow,
  Popper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Rating,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { Tabs, Tab } from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  PhotoCamera as CameraIcon,
  FlipCameraIos as FlipCameraIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Map as MapIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  SubdirectoryArrowRight as SubcategoryIcon,
  CheckCircle as CheckCircleIcon,
  Crop as CropIcon,
  RotateLeft as RotateLeftIcon,
  RotateRight as RotateRightIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Cancel as CancelIcon,
  ShoppingCart as DirectBuyIcon,
  Gavel as AuctionIcon,
  Description as DocumentIcon,
  LocationOn as LocationIcon,
  LocalOffer as PromotionIcon,
  PriceCheck as FixedIcon,
  Inventory2 as MultipleIcon,
  Handshake as NegotiableIcon,
  Close as CloseIcon,
  Man as ManIcon,
  Woman as WomanIcon,
  ChildCare as ChildCareIcon,
  Devices as DevicesIcon,
  Movie as MovieIcon,
  EmojiEvents as EmojiEventsIcon,
  SportsSoccer as SportsSoccerIcon,
  Home as HomeIcon,
  MenuBook as MenuBookIcon,
  Diamond as DiamondIcon,
  Church as ChurchIcon,
  FiberNew as FiberNewIcon,
  History as HistoryIcon,
  BuildCircle as BuildCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AutoAwesome as SparkleIcon,
  Edit as EditIcon,
  SentimentVeryDissatisfied as SentimentVeryDissatisfiedIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  SentimentSatisfied as SentimentSatisfiedIcon,
  SentimentSatisfiedAlt as SentimentSatisfiedAltIcon,
  SentimentVerySatisfied as SentimentVerySatisfiedIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CategoryService } from '../services/categoryService';
import { SubcategoryService } from '../services/subcategoryService';
import { ProductService, CreateProductData, UpdateProductData } from '../services/productService';
import { AuctionService, CreateAuctionData } from '../services/auctionService';
import { AddressService, Address } from '../services/addressService';
import MapSearchNew from '../components/MapSearch/MapSearchNew';
import { Category, Subcategory, supabase } from '../lib/supabase';
import { ChromePicker } from 'react-color';
import ReactCrop, { centerCrop, makeAspectCrop, Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import './ProductUploadPage.css';
import ImageGallery from '../components/ImageGallery';
import MegaMenu, { defaultMegaMenuData } from '../components/Layout/MegaMenu';
import { DeliveryOptionsService, DeliveryOption } from '../services/deliveryOptionsService';
import UserDocumentsModal from '../components/Documents/UserDocumentsModal';
import SettingsModal from '../components/Settings/SettingsModal';
import { PlatformSettingsService, PlatformFeeSettings } from '../services/platformSettingsService';
import { FilterSettingsService } from '../services/filterSettingsService';
import { useTranslation } from '../contexts/TranslationContext';
import { translateWithMyMemory } from '../services/textTranslationService';
import ListingPromotionSelector, { ListingPromotionOption } from '../components/ListingPromotion/ListingPromotionSelector';
import { SubPayOptionsService } from '../services/subPayOptionsService';
import { PayPalService } from '../services/paypalService';
import { PlatformPurchasesService } from '../services/platformPurchasesService';
import {
  calculatePriceWithFeesSync,
  calculateBasePriceFromCustomerPrice,
  calculatePlatformFeeSync
} from '../utils/priceUtils';

interface ProductFormData {
  productType: 'direct-buy' | 'negotiable' | 'auction' | 'promotion';
  saleModel?: 'fixed' | 'negotiable';
  quantityType?: 'single' | 'multiple';
  promotionAddons?: string[];
  name: string;
  description: string;
  category: string;
  subcategory: string;
  brand: string;
  price: number;
  originalPrice: number | undefined;
  condition: 'new' | 'used' | 'refurbished';
  conditionQuality?: number;
  material: string;
  stock: number;
  tags: string[];
  colors: string[];
  customColors: string[];
  sizeSystem: string;
  sizes: string[];
  isActive: boolean;
  isJob: boolean;
  images: string[];
  backblaze_images: string[];
  categories: string[];
  model?: string;
  sku?: string;
  weight?: number;
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  // Auction-specific fields
  auctionId?: string;
  auctionStartTime?: string;
  auctionEndTime?: string;
  auctionReservePrice?: number;
  auctionBidIncrement?: number;
}

// Common materials for all products
const commonMaterials = [
  'Cotton', 'Polyester', 'Wool', 'Silk', 'Leather', 'Denim', 'Linen', 'Cashmere',
  'Spandex', 'Rayon', 'Nylon', 'Acrylic', 'Bamboo', 'Hemp', 'Metal', 'Plastic',
  'Wood', 'Glass', 'Ceramic', 'Stainless Steel', 'Aluminum', 'Carbon Fiber'
];

// Size systems for different product types
const sizeSystems = {
  'men-clothing': {
    name: 'Men\'s Clothing',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL']
  },
  'women-clothing': {
    name: 'Women\'s Clothing',
    sizes: ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL']
  },
  'unisex-clothing': {
    name: 'Unisex Clothing',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL']
  },
  'men-shoes': {
    name: 'Men\'s Shoes',
    sizes: ['6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '14', '14.5', '15']
  },
  'women-shoes': {
    name: 'Women\'s Shoes',
    sizes: ['4', '4.5', '5', '5.5', '6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12']
  },
  'kids-shoes': {
    name: 'Kids\' Shoes',
    sizes: ['10', '10.5', '11', '11.5', '12', '12.5', '13', '13.5', '1', '1.5', '2', '2.5', '3', '3.5', '4', '4.5', '5', '5.5', '6']
  },
  'accessories': {
    name: 'Accessories',
    sizes: ['One Size', 'Small', 'Medium', 'Large', 'Extra Large']
  },
  'electronics': {
    name: 'Electronics',
    sizes: ['Small', 'Medium', 'Large', 'Extra Large', 'One Size']
  },
  'home-garden': {
    name: 'Home & Garden',
    sizes: ['Small', 'Medium', 'Large', 'Extra Large', 'One Size']
  },
  'jewelry': {
    name: 'Jewelry',
    sizes: ['6', '6.5', '7', '7.5', '8', '8.5', '9', '9.5', '10', '10.5', '11', '11.5', '12', 'One Size']
  },
  'watches': {
    name: 'Watches',
    sizes: ['Small (34-38mm)', 'Medium (39-42mm)', 'Large (43-46mm)', 'Extra Large (47mm+)']
  },
  'bags': {
    name: 'Bags & Luggage',
    sizes: ['Small', 'Medium', 'Large', 'Extra Large', 'One Size']
  },
  'sports': {
    name: 'Sports Equipment',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL', 'One Size']
  }
};

const productColors = {
  clothing: ['Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Purple', 'Orange', 'Brown', 'Gray', 'Navy', 'Beige'],
  shoes: ['Black', 'White', 'Brown', 'Tan', 'Gray', 'Navy', 'Red', 'Blue', 'Green', 'Pink'],
  accessories: ['Black', 'White', 'Brown', 'Gold', 'Silver', 'Rose Gold', 'Red', 'Blue', 'Green', 'Pink'],
  electronics: ['Black', 'White', 'Silver', 'Gold', 'Space Gray', 'Rose Gold', 'Blue', 'Red'],
  home: ['White', 'Black', 'Brown', 'Gray', 'Beige', 'Blue', 'Green', 'Red', 'Yellow'],
  default: [
    'Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Pink', 'Purple', 'Orange', 'Brown', 'Gray',
    'Navy', 'Beige', 'Teal', 'Cyan', 'Magenta', 'Indigo', 'Lime', 'Maroon', 'Olive', 'Coral', 'Turquoise', 'Lavender'
  ]
};

const ProductUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, language } = useTranslation();
  const tx = useCallback((key: string, fallback: string) => {
    try {
      const v = t(key);
      return v === key ? fallback : v;
    } catch {
      return fallback;
    }
  }, [t]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [maxVisitedStep, setMaxVisitedStep] = useState(0);
  const [lastEditedStep, setLastEditedStep] = useState(0);
  const activeStepRef = useRef(0);
  const isRestoringFormRef = useRef(false);
  const [platformName, setPlatformName] = useState<string>('');
  const [imageInput, setImageInput] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);
  const restoreDoneRef = useRef(false);

  const [promotionOptions, setPromotionOptions] = useState<ListingPromotionOption[]>([]);
  const [promotionOptionsLoading, setPromotionOptionsLoading] = useState(false);
  const [promotionOptionsError, setPromotionOptionsError] = useState<string | null>(null);
  const [promotionOptionsLoadedOnce, setPromotionOptionsLoadedOnce] = useState(false);
  const [resolvedPromotionOptions, setResolvedPromotionOptions] = useState<ReturnType<typeof SubPayOptionsService.resolveActive> | null>(null);
  const [paypalProcessing, setPaypalProcessing] = useState(false);

  const FORM_STORAGE_KEY = 'productUploadFormData:v1';
  const STEP_STORAGE_KEY = 'productUploadActiveStep:v1';
  const SHIPPING_STORAGE_KEY = 'productUploadSelectedShipping:v1';
  const COMPLETED_STEPS_KEY = 'productUploadCompletedSteps:v1';
  const savedSubcategoryRef = useRef<string | null>(null);
  const SHIPPING_MODE_KEY = 'productUploadShippingMode:v1';
  const [userKeyPrefix, setUserKeyPrefix] = useState('');
  const k = useCallback((key: string) => (userKeyPrefix ? `${userKeyPrefix}${key}` : key), [userKeyPrefix]);

  const goToStep = useCallback((step: number) => {
    const next = Math.max(0, Math.min(step, 7));
    setActiveStep(next);
    setMaxVisitedStep((prev) => Math.max(prev, next));
    try { localStorage.setItem(k(STEP_STORAGE_KEY), String(next)); } catch {}
  }, [k]);

  const loadPromotionOptions = useCallback(async () => {
    setPromotionOptionsLoading(true);
    setPromotionOptionsError(null);
    try {
      const doc = await SubPayOptionsService.getOptions();
      const resolved = SubPayOptionsService.resolveActive(doc);
      const currency = resolved.currency || 'EUR';
      setResolvedPromotionOptions(resolved);
      const opts = (resolved.options || [])
        .filter((o) => o && o.enabled !== false)
        .map((o) => ({
          id: String(o.id),
          title: String(o.title || ''),
          description: String(o.description || ''),
          price: Number(o.price) || 0,
          currency,
          badge: o.badge,
          recommended: !!o.recommended,
        }));
      setPromotionOptions(opts);
      setPromotionOptionsLoadedOnce(true);
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : 'Failed to load boost options';
      console.error('Failed to load boost options:', e);
      setPromotionOptions([]);
      setResolvedPromotionOptions(null);
      setPromotionOptionsError(msg);
      setPromotionOptionsLoadedOnce(true);
    } finally {
      setPromotionOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeStep !== 6) return;
    if (promotionOptionsLoadedOnce) return;
    loadPromotionOptions();
  }, [activeStep, promotionOptionsLoadedOnce, loadPromotionOptions]);

  useEffect(() => {
    if (activeStep !== 7) return;
    if (promotionOptionsLoadedOnce) return;
    loadPromotionOptions();
  }, [activeStep, promotionOptionsLoadedOnce, loadPromotionOptions]);

  const openPayPalForBoost = useCallback(
    async (input: { amount: number; currency: string; platformEmail: string }): Promise<void> => {
      if (paypalProcessing) return;
      setPaypalProcessing(true);
      try {
        const result = await PayPalService.createPaymentOrder({
          vendorEmail: input.platformEmail,
          vendorAmount: input.amount,
          platformEmail: input.platformEmail,
          platformAmount: 0,
          currency: input.currency,
        });

        if (!result.success || !result.approvalUrl || !result.orderId) {
          throw new Error(result.error || 'Failed to create PayPal order');
        }

        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const paypalWindow = window.open(
          result.approvalUrl,
          'PayPal Payment',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!paypalWindow) {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        toast.success('Opening PayPal...');

        await new Promise<void>((resolve, reject) => {
          let done = false;

          const cleanup = () => {
            window.removeEventListener('message', onMessage);
            if (interval) window.clearInterval(interval);
            if (timeout) window.clearTimeout(timeout);
          };

          const succeed = () => {
            if (done) return;
            done = true;
            cleanup();
            resolve();
          };

          const fail = (err: any) => {
            if (done) return;
            done = true;
            cleanup();
            reject(err);
          };

          const checkStatus = async (): Promise<'completed' | 'cancelled' | 'pending'> => {
            const statusResult = await PayPalService.checkPaymentStatus(result.orderId as string);
            if (statusResult.success && statusResult.completed) {
              succeed();
              return 'completed';
            }
            if (statusResult.success && statusResult.cancelled) {
              fail(new Error('Payment was cancelled'));
              return 'cancelled';
            }
            return 'pending';
          };

          const waitForCompletion = async (opts?: { attempts?: number; delayMs?: number }): Promise<'completed' | 'cancelled' | 'pending'> => {
            const attempts = Math.max(1, opts?.attempts ?? 8);
            const delayMs = Math.max(250, opts?.delayMs ?? 900);
            for (let i = 0; i < attempts; i++) {
              const s = await checkStatus();
              if (s !== 'pending') return s;
              await new Promise((r) => window.setTimeout(r, delayMs));
            }
            return 'pending';
          };

          const onMessage = async (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'PAYMENT_CANCELLED') {
              fail(new Error('Payment was cancelled'));
              return;
            }
            if (event.data?.type === 'PAYMENT_SUCCESS') {
              try {
                await waitForCompletion({ attempts: 10, delayMs: 750 });
              } catch (e) {
                fail(e);
              }
            }
          };

          window.addEventListener('message', onMessage);

          const interval = window.setInterval(async () => {
            try {
              if (paypalWindow.closed) {
                const s = await waitForCompletion({ attempts: 10, delayMs: 750 });
                if (s === 'completed') return;
                if (s === 'cancelled') return;
                fail(new Error('Payment window was closed'));
              }
            } catch {
              // ignore
            }
          }, 1000);

          const timeout = window.setTimeout(() => {
            try {
              if (!paypalWindow.closed) paypalWindow.close();
            } catch {
              // ignore
            }
            fail(new Error('Payment timed out. Please try again.'));
          }, 10 * 60 * 1000);
        });
      } finally {
        setPaypalProcessing(false);
      }
    },
    [paypalProcessing]
  );

  const handlePublishClick = async (formData: ProductFormData) => {
    if (isUploading || paypalProcessing) return;

    const selectedIds = Array.isArray(formData.promotionAddons) ? formData.promotionAddons : [];
    const currency = (resolvedPromotionOptions?.currency || 'EUR').trim() || 'EUR';
    const byId = new Map((promotionOptions || []).map((o) => [String(o.id), o] as const));
    const total = selectedIds.reduce((sum, id) => sum + (Number(byId.get(String(id))?.price) || 0), 0);

    if (total > 0) {
      let platformEmail = '';
      try {
        platformEmail = await PlatformSettingsService.getPlatformPayPalEmail();
      } catch (e: any) {
        console.error('Failed to resolve platform PayPal email (will defer to edge function):', e);
        platformEmail = '';
      }

      try {
        await openPayPalForBoost({ amount: total, currency, platformEmail });
      } catch (e: any) {
        toast.error(e?.message || 'Payment failed');
        return;
      }
    }

    await onSubmit(formData);
  };

  const showStepToast = useCallback((opts: { id: string; message: string; step: number; label: string; }) => {
    toast.dismiss(opts.id);
    toast.custom(
      (t) => (
        <Box
          sx={{
            width: { xs: 320, sm: 380 },
            maxWidth: '92vw',
            bgcolor: 'rgba(17, 17, 17, 0.94)',
            color: 'white',
            borderRadius: 3,
            boxShadow: '0 14px 34px rgba(0,0,0,0.35)',
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            border: '1px solid rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, mb: 0.5 }}>
              {opts.message}
            </Typography>
            <Typography sx={{ opacity: 0.8, fontSize: 12, lineHeight: 1.1 }}>
              {opts.label}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              toast.dismiss(t.id);
              goToStep(opts.step);
            }}
            sx={{
              borderRadius: 999,
              px: 1.5,
              py: 0.75,
              fontWeight: 800,
              textTransform: 'none',
              background: 'linear-gradient(135deg, rgba(255,122,0,1), rgba(255,72,0,1))',
              boxShadow: '0 10px 18px rgba(255,96,0,0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, rgba(255,140,32,1), rgba(255,92,32,1))',
              }
            }}
          >
            {tx('productUpload.nav.goToStep', 'Go to step')}
          </Button>
          <IconButton
            onClick={() => toast.dismiss(t.id)}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.75)' }}
            aria-label="close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      { id: opts.id, duration: 6000 }
    );
  }, [goToStep, tx]);
  
  // State for categories and subcategories
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [categoryTranslations, setCategoryTranslations] = useState<Record<string, { name: string; description?: string }>>({});
  const [subcategoryTranslations, setSubcategoryTranslations] = useState<Record<string, { name: string; description?: string }>>({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  const [subcategoriesError, setSubcategoriesError] = useState<string | null>(null);
  const [categoriesFetched, setCategoriesFetched] = useState(false);
  const [subcategoriesFetched, setSubcategoriesFetched] = useState(false);
  
  // State for color picker
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [currentCustomColor, setCurrentCustomColor] = useState('#000000');
  
  // State for image cropping
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImage, setCropImage] = useState<string>('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [scale, setScale] = useState(1);
  const [rotate, setRotate] = useState(0);
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const imageProcessedRef = useRef(false);
  
  // State for image gallery
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // State for upload progress and success
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [uploadedProduct, setUploadedProduct] = useState<any>(null);
  const [wipProductId, setWipProductId] = useState<string | null>(null);
  const [isEnsuringWip, setIsEnsuringWip] = useState(false);
  const ensuringWipPromiseRef = useRef<Promise<string | null> | null>(null);
  const [categoryPickerOpen, setCategoryPickerOpen] = useState(false);
  const [categoryTop, setCategoryTop] = useState<string | null>('Damen');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [newSparkleTick, setNewSparkleTick] = useState(0);

  const [categoriesEnabled, setCategoriesEnabled] = useState<Record<string, boolean>>({});
  const [subcategoriesEnabled, setSubcategoriesEnabled] = useState<Record<string, Record<string, boolean>>>({});
  
  // State for documents modal
  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  
  // Platform fee settings and pricing mode
  const [platformFeeSettings, setPlatformFeeSettings] = useState<PlatformFeeSettings | null>(null);
  const [pricingMode, setPricingMode] = useState<'base' | 'customer'>('base'); // 'base' = set base price, 'customer' = set customer price
  // Website settings (to control available sale options)
  const [productOptionsEnabled, setProductOptionsEnabled] = useState<{ [k: string]: boolean }>({
    fixed_single: true,
    fixed_multiple: true,
    negotiable_single: true,
    negotiable_multiple: true,
    auction: true,
    promotion: true,
  });
  const [websiteSettingsLoaded, setWebsiteSettingsLoaded] = useState(false);

  // Camera capture state
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment'); // 'user' = front, 'environment' = back

  const [b2Items, setB2Items] = useState<Array<{ key: string; size: number; lastModified: string; url?: string }>>([]);
  const [b2NextToken, setB2NextToken] = useState<string | null>(null);
  const [b2Loading, setB2Loading] = useState<boolean>(false);
  const [b2Enabled, setB2Enabled] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    getValues,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<ProductFormData>({
    defaultValues: {
      productType: 'direct-buy' as const,
      saleModel: 'fixed',
      quantityType: 'single',
      promotionAddons: [],
      name: '',
      description: '',
      category: '',
      subcategory: '',
      brand: '',
      price: 0,
      originalPrice: undefined,
      condition: 'new' as const,
      material: '',
      stock: 0,
      tags: [],
      colors: [],
      customColors: [],
      sizeSystem: '',
      sizes: [],
      isActive: true,
      isJob: false,
      images: [],
      backblaze_images: [],
      categories: [],
      model: '',
      sku: '',
      weight: undefined,
      dimensions: undefined,
      // Auction-specific fields
      auctionId: undefined,
      auctionStartTime: undefined,
      auctionEndTime: undefined,
      auctionReservePrice: undefined,
      auctionBidIncrement: 1.0,
    },
  });

  useEffect(() => {
    activeStepRef.current = activeStep;
  }, [activeStep]);

  // Remove broken image URLs from previews and payload
  const handleImageError = useCallback((badUrl: string) => {
    const currImgs: string[] = getValues('images') || [];
    const nextImgs = currImgs.filter((u) => u !== badUrl);
    if (nextImgs.length !== currImgs.length) setValue('images', nextImgs);
    const currB2: string[] = (getValues('backblaze_images') || []);
    const nextB2 = currB2.filter((u) => u !== badUrl);
    if (nextB2.length !== currB2.length) setValue('backblaze_images', nextB2);
  }, [getValues, setValue]);

  const watchedProductType = watch('productType');
  const watchedCategory = watch('category');
  const watchedImages = watch('images');
  const watchedTags = watch('tags');
  const watchedColors = watch('colors');
  const watchedCustomColors = watch('customColors');
  const watchedSizeSystem = watch('sizeSystem');
  const watchedSizes = watch('sizes');
  const watchedPrice = watch('price');
  const watchedSaleModel = watch('saleModel');
  const watchedQuantityType = watch('quantityType');
  const watchedCategoriesArr = watch('categories');

  const steps = [
    t('productUpload.steps.productType'),
    t('productUpload.steps.basicInfo'),
    t('productUpload.steps.details'),
    t('productUpload.steps.images'),
    t('productUpload.steps.shipping'),
    t('productUpload.steps.location'),
    tx('productUpload.steps.boostVisibility', 'Boost & Visibility'),
    t('productUpload.steps.reviewPublish')
  ];

  // Icon for top-level categories in the picker
  const getTopCatIcon = useCallback((label: string) => {
    const l = (label || '').toLowerCase();
    if (l.includes('damen') || l.includes('women') || l.includes('frau')) return <WomanIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('herren') || l.includes('men') || l.includes('herr')) return <ManIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('kinder') || l.includes('kids') || l.includes('kind')) return <ChildCareIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('elektronik') || l.includes('electronics')) return <DevicesIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('unterhaltung') || l.includes('entertainment')) return <MovieIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('sport')) return <SportsSoccerIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('home') || l.includes('wohnen') || l.includes('haus')) return <HomeIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('bücher') || l.includes('buecher') || l.includes('books')) return <MenuBookIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('designer') || l.includes('design')) return <DiamondIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('hobby') || l.includes('sammler') || l.includes('collector')) return <EmojiEventsIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    if (l.includes('tradition')) return <ChurchIcon fontSize="small" sx={{ color: 'primary.main' }} />;
    return <CategoryIcon fontSize="small" sx={{ color: 'primary.main' }} />;
  }, []);

  // Determine if selected category paths are apparel/textile related
  const isTextilePath = useCallback((path: string) => {
    const s = (path || '').toLowerCase();
    const segs = s.split('/').map((x) => x.trim());
    const top = segs[0] || '';
    const apparelTop = ['damen', 'herren', 'kinder', 'designer', 'designerartikel', 'traditionell'];
    if (apparelTop.includes(top)) return true;
    const keywords = [
      'kleidung', 'bekleidung', 'mode', 'textil', 'stoffe', 'schuhe', 'taschen', 'accessoires',
      'jacken', 'hosen', 'shirt', 'shirts', 'kleid', 'röcke', 'rock', 'pullover', 'strick',
      'unterwäsche', 'sportbekleidung'
    ];
    return segs.some((seg) => keywords.some((k) => seg.includes(k)));
  }, []);

  const showMaterialSection = useMemo(() => {
    const arr = Array.isArray(watchedCategoriesArr) ? watchedCategoriesArr : [];
    if (arr.length > 0) return arr.some(isTextilePath);
    const cat = (getValues('category') as string) || '';
    const sub = (getValues('subcategory') as string) || '';
    const path = sub ? `${cat} / ${sub}` : cat;
    return path ? isTextilePath(path) : false;
  }, [watchedCategoriesArr, isTextilePath]);

  const inferredSizeSystem = useMemo(() => {
    const rawPaths: string[] = (() => {
      const arr = Array.isArray(watchedCategoriesArr) ? watchedCategoriesArr : [];
      if (arr.length > 0) return arr;
      const cat = (getValues('category') as string) || '';
      const sub = (getValues('subcategory') as string) || '';
      const path = sub ? `${cat} / ${sub}` : cat;
      return path ? [path] : [];
    })();

    const s = rawPaths.join(' / ').toLowerCase();

    const isMen = /\bmen\b|\bherren\b|\bmann\b/.test(s);
    const isWomen = /\bwomen\b|\bdamen\b|\bfrau\b/.test(s);
    const isKids = /\bkids\b|\bkinder\b|\bchild\b|\bbaby\b/.test(s);

    const isShoes = /\bshoes\b|\bschuhe\b|\bsneaker\b|\bboot\b/.test(s);
    const isClothing = /\bclothing\b|\bkleidung\b|\bbekleidung\b|\bapparel\b|\bmode\b/.test(s) || rawPaths.some(isTextilePath);

    const isElectronics = /\belectronics\b|\belektronik\b|\btablet\b|\bphone\b|\bsmartphone\b|\blaptop\b|\bcomputer\b/.test(s);
    const isHomeGarden = /\bhome\b|\bgarden\b|\bhaus\b|\bwohnen\b|\bgarten\b|\bm\u00f6bel\b|\bdeko\b/.test(s);
    const isJewelry = /\bjewelry\b|\bschmuck\b|\bring\b|\bhalskette\b|\barmband\b/.test(s);
    const isWatches = /\bwatches\b|\buhr\b|\buhren\b/.test(s);
    const isBags = /\bbags\b|\bluggage\b|\btasche\b|\btaschen\b|\bkoffer\b/.test(s);
    const isSports = /\bsports\b|\bsport\b|\bfitness\b|\bgym\b/.test(s);
    const isAccessories = /\baccessories\b|\baccessoires\b|\bzubeh\u00f6r\b|\bzubehor\b/.test(s);

    if (isShoes) {
      if (isKids) return 'kids-shoes';
      if (isWomen) return 'women-shoes';
      if (isMen) return 'men-shoes';
      return 'men-shoes';
    }
    if (isClothing) {
      if (isWomen) return 'women-clothing';
      if (isMen) return 'men-clothing';
      return 'unisex-clothing';
    }
    if (isWatches) return 'watches';
    if (isJewelry) return 'jewelry';
    if (isBags) return 'bags';
    if (isSports) return 'sports';
    if (isHomeGarden) return 'home-garden';
    if (isElectronics) return 'electronics';
    if (isAccessories) return 'accessories';

    return '';
  }, [watchedCategoriesArr, getValues, isTextilePath]);

  const allowedSizeSystemKeys = useMemo(() => {
    if (!inferredSizeSystem) return Object.keys(sizeSystems);
    return [inferredSizeSystem];
  }, [inferredSizeSystem]);

  useEffect(() => {
    const catsArr = Array.isArray(watchedCategoriesArr) ? watchedCategoriesArr : [];
    const cat = (getValues('category') as string) || '';
    const sub = (getValues('subcategory') as string) || '';
    const hasCat = catsArr.length > 0 || !!cat || !!sub;
    if (!hasCat) return;
    if (!inferredSizeSystem) return;

    const current = (getValues('sizeSystem') as string) || '';
    if (current !== inferredSizeSystem) {
      setValue('sizeSystem', inferredSizeSystem);
      setValue('sizes', []);
    }
  }, [watchedCategoriesArr, inferredSizeSystem, getValues, setValue]);

  // Does the user have at least one category selected?
  const hasSelectedCategory = useMemo(() => {
    const catsArr = Array.isArray(watchedCategoriesArr) ? watchedCategoriesArr : [];
    const nonEmptyCats = catsArr.filter((x) => String(x || '').trim().length > 0);
    return nonEmptyCats.length > 0;
  }, [watchedCategoriesArr, watch('category'), watch('subcategory')]);

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, [userKeyPrefix]);

  // Load current user to prefix localStorage keys per-user
  useEffect(() => {
    const loadUser = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        setUserKeyPrefix(user?.id ? `u:${user.id}:` : 'anon:');
      } catch {}
    };
    loadUser();
  }, []);

  // Load website-settings.json for product options
  useEffect(() => {
    const loadWebsiteSettings = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.branding?.platformName && typeof data.branding.platformName === 'string') {
          setPlatformName(data.branding.platformName);
        }
        if (data?.categoriesEnabled && typeof data.categoriesEnabled === 'object') {
          setCategoriesEnabled(data.categoriesEnabled);
        }
        if (data?.subcategoriesEnabled && typeof data.subcategoriesEnabled === 'object') {
          setSubcategoriesEnabled(data.subcategoriesEnabled);
        }
        if (data?.productOptions && typeof data.productOptions === 'object') {
          setProductOptionsEnabled((prev) => ({ ...prev, ...data.productOptions }));
          const anyDirect = Boolean(data.productOptions.fixed_single || data.productOptions.fixed_multiple);
          const anyNegotiableType = Boolean(data.productOptions.negotiable_single || data.productOptions.negotiable_multiple);
          const anyFixed = Boolean(data.productOptions.fixed_single || data.productOptions.fixed_multiple);
          const anyAuction = data.productOptions.auction !== false;
          const anyPromotion = data.productOptions.promotion !== false;

          const currType = getValues('productType');
          const currSale = getValues('saleModel');
          const currQty = getValues('quantityType');

          // Ensure productType is valid
          if (currType === 'auction' && !anyAuction) {
            if (anyDirect) setValue('productType', 'direct-buy');
            else if (anyNegotiableType) setValue('productType', 'negotiable');
            else if (anyPromotion) setValue('productType', 'promotion');
          }
          if (currType === 'promotion' && !anyPromotion) {
            if (anyDirect) setValue('productType', 'direct-buy');
            else if (anyNegotiableType) setValue('productType', 'negotiable');
            else if (anyAuction) setValue('productType', 'auction');
          }
          if (currType === 'direct-buy' && !anyDirect) {
            if (anyNegotiableType) setValue('productType', 'negotiable');
            else if (anyAuction) setValue('productType', 'auction');
            else if (anyPromotion) setValue('productType', 'promotion');
          }
          if (currType === 'negotiable' && !anyNegotiableType) {
            if (anyDirect) setValue('productType', 'direct-buy');
            else if (anyAuction) setValue('productType', 'auction');
            else if (anyPromotion) setValue('productType', 'promotion');
          }

          // Ensure direct-buy sub-options are valid (DIRECT BUY = fixed only)
          const nextType = getValues('productType');
          if (nextType === 'direct-buy') {
            if (currSale !== 'fixed') setValue('saleModel', 'fixed');
            const nextSale = 'fixed';
            const fixedSingleOk = data.productOptions.fixed_single !== false;
            const fixedMultipleOk = data.productOptions.fixed_multiple !== false;

            if (nextSale === 'fixed') {
              if (currQty === 'single' && !fixedSingleOk) setValue('quantityType', 'multiple');
              if (currQty === 'multiple' && !fixedMultipleOk) setValue('quantityType', 'single');
            }
          }

          // Ensure negotiable productType sub-options are valid (VERHANDLUNGSBASIS = negotiable only)
          if (nextType === 'negotiable') {
            if (currSale !== 'negotiable') setValue('saleModel', 'negotiable');
            const negSingleOk = data.productOptions.negotiable_single !== false;
            const negMultipleOk = data.productOptions.negotiable_multiple !== false;
            if (currQty === 'single' && !negSingleOk) setValue('quantityType', 'multiple');
            if (currQty === 'multiple' && !negMultipleOk) setValue('quantityType', 'single');
          }
        }
        setB2Enabled(data?.uploadTarget === 'backblaze');
      } catch {
      } finally {
        setWebsiteSettingsLoaded(true);
      }
    };
    loadWebsiteSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WIP creation now happens after selecting product type and proceeding from step 1
  // Additionally, ensure a WIP draft exists when the page opens or when user resets to start from scratch

  const ensureWipDraft = useCallback(async (opts?: { silent?: boolean }) => {
    const KEY = 'currentWipProductId';
    const silent = !!opts?.silent;
    try {
      if (ensuringWipPromiseRef.current) {
        return ensuringWipPromiseRef.current;
      }
      setIsEnsuringWip(true);
      const run = (async (): Promise<string | null> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) return null;

      let cached: string | null = null;
      try { cached = sessionStorage.getItem(KEY); } catch {}
      if (cached && cached.length > 0) {
        try {
          const { data: existingCached } = await supabase
            .from('products')
            .select('id, product_status, seller_id')
            .eq('id', cached)
            .eq('seller_id', user.id)
            .maybeSingle();
          if (existingCached?.id && existingCached.product_status === 'WIP') {
            if (!wipProductId) setWipProductId(existingCached.id);
            return existingCached.id;
          }
        } catch {}
        try { sessionStorage.removeItem(KEY); } catch {}
      }

      let existingWipId: string | null = null;
      try {
        const { data: existing } = await supabase
          .from('products')
          .select('id, product_status')
          .eq('seller_id', user.id)
          .eq('product_status', 'WIP')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (existing?.id) existingWipId = existing.id;
      } catch {}

      if (existingWipId) {
        setWipProductId(existingWipId);
        try { sessionStorage.setItem(KEY, existingWipId); } catch {}
        if (!silent) toast.success(t('productUpload.toast.draftReused') || 'Draft listing reused');
        return existingWipId;
      }

      const currentType = getValues('productType');
      const pt = (currentType === 'auction') ? 'a' : (currentType === 'promotion' ? 'p' : 'd');
      const negotiable = currentType === 'negotiable' || getValues('saleModel') === 'negotiable';
      let createdId: string | null = null;
      try {
        const wip = await ProductService.createWipProduct({ product_type: pt as any, is_negotiable: negotiable });
        if (wip?.id) createdId = wip.id;
      } catch {}

      if (!createdId) {
        try {
          const { data: created, error: createErr } = await supabase
            .from('products')
            .insert({ seller_id: user.id, product_status: 'WIP', is_active: false })
            .select()
            .single();
          if (!createErr && created?.id) createdId = created.id;
        } catch {}
      }

      if (createdId) {
        setWipProductId(createdId);
        try { sessionStorage.setItem(KEY, createdId); } catch {}
        if (!silent) toast.success(t('productUpload.toast.draftCreated') || 'Draft listing created');
        return createdId;
      }
      return null;
      })();
      ensuringWipPromiseRef.current = run;
      const result = await run;
      return result;
    } catch (e) {
      if (!silent) toast.error(t('productUpload.toast.draftCreateFailed') || 'Failed to ensure draft listing');
      return null;
    } finally {
      ensuringWipPromiseRef.current = null;
      setIsEnsuringWip(false);
    }
  }, [getValues, t, wipProductId]);

  // Ensure a WIP draft exists on initial page load
  useEffect(() => {
    // Run silently at page open so users can start immediately
    ensureWipDraft({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMyBackblazeImages = async (mode: 'reset' | 'next' = 'reset') => {
    if (!b2Enabled) return;
    if (mode === 'next' && !b2NextToken) return;
    setB2Loading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) { setB2Loading(false); return; }
      const payload: any = { signRead: true, maxKeys: 100 };
      if (mode === 'next' && b2NextToken) payload.continuationToken = b2NextToken;
      const res = await fetch('/api/b2-list.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) {
        throw new Error(json?.error || raw || 'Failed');
      }
      const items = Array.isArray(json.items) ? json.items : [];
      // Keep signed URL if provided by API for reliable preview; else fallback to public path
      const base: string | null = typeof json.publicBaseUrl === 'string' ? json.publicBaseUrl : null;
      const mapped = items.map((it: any) => {
        const preferred = it.url && typeof it.url === 'string' && it.url.length > 0
          ? it.url
          : (base ? `${base.replace(/\/$/, '')}/${encodeURIComponent(it.key).replace(/%2F/g,'/')}` : undefined);
        return { ...it, url: preferred };
      });
      setB2Items((prev) => mode === 'reset' ? mapped : [...prev, ...mapped]);
      setB2NextToken(json.nextContinuationToken || null);
      // Merge fetched URLs into form images/backblaze_images for preview
      const urls: string[] = mapped.map((it: any) => it.url).filter(Boolean);
      if (urls.length > 0) {
        const currImgs: string[] = getValues('images') || [];
        const mergedImgs = Array.from(new Set([...currImgs, ...urls]));
        setValue('images', mergedImgs);
        const currB2: string[] = (getValues('backblaze_images') || []);
        const mergedB2 = Array.from(new Set([...currB2, ...urls]));
        setValue('backblaze_images', mergedB2);
      }
    } catch {
    } finally {
      setB2Loading(false);
    }
  };

  // Auto-load Backblaze images when enabled on first load
  useEffect(() => {
    if (b2Enabled) {
      fetchMyBackblazeImages('reset');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b2Enabled]);

  const deleteBackblazeImage = async (key: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return;
      const res = await fetch('/api/b2-delete.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ key }),
      });
      const raw = await res.text();
      let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
      if (!res.ok || !json?.ok) throw new Error(json?.error || raw || 'Failed');
      setB2Items((prev) => prev.filter((it) => it.key !== key));
      const currentImgs = (getValues('images') || []).filter((u: string) => !u.includes(encodeURIComponent(key)) && !u.endsWith('/' + key));
      setValue('images', currentImgs);
      const currentB2 = (getValues('backblaze_images') || []).filter((u: string) => !u.includes(encodeURIComponent(key)) && !u.endsWith('/' + key));
      setValue('backblaze_images', currentB2);
      toast.success('Deleted');
    } catch (e:any) {
      toast.error(e?.message || 'Delete failed');
    }
  };

  // Translate categories when language changes or categories fetched
  useEffect(() => {
    const run = async () => {
      if (!categories || categories.length === 0) return;
      if (language === 'en') { setCategoryTranslations({}); return; }
      const entries = await Promise.all(categories.map(async (c) => {
        const name = await translateWithMyMemory(c.name || '', language as any, 'en');
        const desc = c.description ? await translateWithMyMemory(c.description, language as any, 'en') : undefined;
        return [c.id, { name, description: desc }] as const;
      }));
      setCategoryTranslations(Object.fromEntries(entries));
    };
    run();
  }, [categories, language]);

  // Translate subcategories when language changes or subcategories fetched
  useEffect(() => {
    const run = async () => {
      if (!subcategories || subcategories.length === 0) { setSubcategoryTranslations({}); return; }
      if (language === 'en') { setSubcategoryTranslations({}); return; }
      const entries = await Promise.all(subcategories.map(async (s) => {
        const name = await translateWithMyMemory(s.name || '', language as any, 'en');
        const desc = s.description ? await translateWithMyMemory(s.description, language as any, 'en') : undefined;
        return [s.id, { name, description: desc }] as const;
      }));
      setSubcategoryTranslations(Object.fromEntries(entries));
    };
    run();
  }, [subcategories, language]);

  // Load platform fee settings on mount
  useEffect(() => {
    const loadFeeSettings = async () => {
      try {
        const settings = await PlatformSettingsService.getPlatformFeeSettings();
        setPlatformFeeSettings(settings);
      } catch (error) {
        console.error('Error loading platform fee settings:', error);
        // Set default fee settings if loading fails
        setPlatformFeeSettings({
          fee_type: 'fixed',
          fee_amount: 0.50,
          fee_percentage: null,
        });
      }
    };
    loadFeeSettings();
  }, []);

  // Handle URL parameter for auction type preselection
  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (typeParam === 'auction') {
      setValue('productType', 'auction');
    }
  }, [searchParams, setValue]);

  // Restore persisted wizard state on mount (per-user)
  useEffect(() => {
    if (restoreDoneRef.current) return;
    if (!userKeyPrefix) return; // wait until prefix is known
    restoreDoneRef.current = true;
    isRestoringFormRef.current = true;
    try {
      const savedForm = localStorage.getItem(k(FORM_STORAGE_KEY));
      if (savedForm) {
        const parsed = JSON.parse(savedForm);
        Object.entries(parsed || {}).forEach(([key, value]) => {
          // @ts-ignore
          setValue(key, value);
        });
        // Keep subcategory to re-apply after subcategories load
        if (parsed && parsed.subcategory) {
          savedSubcategoryRef.current = parsed.subcategory;
        }
      }
    } catch {}
    try {
      // We'll compute earliest incomplete step below; use saved step only if all are complete
    } catch {}
    try {
      const savedShipping = localStorage.getItem(k(SHIPPING_STORAGE_KEY));
      if (savedShipping) setSelectedDeliveryOptionIds(JSON.parse(savedShipping));
    } catch {}
    try {
      const savedShippingMode = localStorage.getItem(k(SHIPPING_MODE_KEY));
      if (savedShippingMode === 'pickup' || savedShippingMode === 'shipping') {
        setShippingMode(savedShippingMode as 'pickup' | 'shipping');
      }
    } catch {}
    try {
      const savedCompletedSteps = localStorage.getItem(k(COMPLETED_STEPS_KEY));
      if (savedCompletedSteps) setCompletedSteps(JSON.parse(savedCompletedSteps));
    } catch {}

    // Compute earliest incomplete step and navigate there silently
    try {
      const allSteps = [0,1,2,3,4,5,6,7];
      const firstIncomplete = allSteps.find((s) => !validateCurrentStep(s, true));
      if (typeof firstIncomplete === 'number') {
        setActiveStep(firstIncomplete);
        setMaxVisitedStep(firstIncomplete);
      } else {
        // All steps pass silent validation; keep at review step
        setActiveStep(7);
        setMaxVisitedStep(7);
      }
    } catch {}
    isRestoringFormRef.current = false;
  }, []);

  useEffect(() => {
    setMaxVisitedStep((prev) => Math.max(prev, activeStep));
  }, [activeStep]);

  useEffect(() => {
    // Update lastEditedStep ONLY when the user actually changes a field (not when restoring)
    const subscription = watch((_value, meta) => {
      if (isRestoringFormRef.current) return;
      if (!meta?.name) return;
      const type = (meta as any)?.type;
      if (type === 'change' || type === 'blur' || type === 'setValue' || type == null) {
        setLastEditedStep(activeStepRef.current);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (!Array.isArray(completedSteps) || completedSteps.length === 0) return;
    const m = Math.max(...completedSteps);
    if (Number.isFinite(m)) setMaxVisitedStep((prev) => Math.max(prev, m));
  }, [completedSteps]);

  // Subcategories are no longer used with the MegaMenu selector.
  useEffect(() => {
    setSubcategories([]);
    setValue('subcategory', '');
    setSubcategoriesFetched(false);
  }, [watchedCategory]);

  // Fetch categories on component mount
  const fetchCategories = async () => {
    if (categoriesFetched) return; // Don't fetch if already fetched
    
    try {
      setCategoriesLoading(true);
      setCategoriesError(null);
      console.log('Fetching categories...');
      const fetchedCategories = await CategoryService.getProductCategories();
      console.log('Fetched categories:', fetchedCategories);
      console.log('Number of categories fetched:', fetchedCategories.length);
      
      // Filter for active categories only
      const activeCategories = fetchedCategories.filter(cat => cat.is_active);
      setCategories(activeCategories);
      setCategoriesFetched(true);
      
      if (activeCategories.length === 0) {
        setCategoriesError('No active categories found in the database. Please contact an administrator to add active categories.');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      const errorMessage = 'Failed to load categories from database. Please check your connection and try again.';
      setCategoriesError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Fetch subcategories when category is selected
  const fetchSubcategories = async (_desiredSubcategoryId?: string) => {
    // No-op: subcategories not fetched; rely on MegaMenu structure
    setSubcategories([]);
    setValue('subcategory', '');
    setSubcategoriesFetched(true);
    setSubcategoriesLoading(false);
    setSubcategoriesError(null);
    return;
  };


  const validateCurrentStep = (step: number, silent?: boolean): boolean => {
    const formData = watch();
    const maybeToast = (msg: string) => { if (!silent) toast.error(msg); };
    
    switch (step) {
      case 0: // Product Type Selection
        if (!formData.productType) {
          maybeToast(t('productUpload.errors.selectProductType'));
          return false;
        }
        return true;
        
      case 1: // Basic Info
        if (!formData.name || formData.name.length < 3) {
          maybeToast(t('productUpload.errors.nameMin'));
          return false;
        }
        if (!formData.description || formData.description.length < 10) {
          maybeToast(t('productUpload.errors.descriptionMin'));
          return false;
        }
        if (!formData.brand) {
          maybeToast(t('productUpload.errors.enterBrand'));
          return false;
        }
        // Require at least one category path
        {
          const catsArr = Array.isArray(formData.categories) ? formData.categories : [];
          const nonEmptyCats = catsArr.filter((x: any) => String(x || '').trim().length > 0);
          if (nonEmptyCats.length === 0) {
            maybeToast(tx('productUpload.errors.selectAtLeastOneCategory', 'Please select at least one category'));
            return false;
          }
        }
        
        // Auction-specific validation (only validate if user has started filling auction fields)
        if (formData.productType === 'auction') {
          // Check if any auction field has been touched/filled (not just default values)
          const hasAnyAuctionData = (formData.price && formData.price !== undefined && formData.price !== 0) ||
                                   (formData.auctionBidIncrement && formData.auctionBidIncrement !== undefined && formData.auctionBidIncrement !== 1.00) || 
                                   formData.auctionEndTime || 
                                   (formData.auctionReservePrice && formData.auctionReservePrice !== undefined);
          
          // Only validate auction fields if user has started filling them
          if (hasAnyAuctionData) {
            // Validate minimum bid price (main price field for auctions)
            if (!formData.price || formData.price <= 0) {
              maybeToast(t('productUpload.errors.auctionMinPrice'));
              return false;
            }
            if (!formData.auctionBidIncrement || formData.auctionBidIncrement <= 0) {
              maybeToast(t('productUpload.errors.auctionBidIncrement'));
              return false;
            }
            if (!formData.auctionEndTime) {
              maybeToast(t('productUpload.errors.auctionEndTime'));
              return false;
            }
            // Check if auction end time is in the future
            const endTime = new Date(formData.auctionEndTime);
            const now = new Date();
            if (endTime <= now) {
              maybeToast(t('productUpload.errors.auctionEndFuture'));
              return false;
            }
            // Check if reserve price is higher than starting price (if provided)
            if (formData.auctionReservePrice && formData.auctionReservePrice <= formData.price) {
              maybeToast(t('productUpload.errors.reserveHigherThanStart'));
              return false;
            }
          }
        }
        
        return true;
        
      case 2: // Details
        // For auctions, price validation is handled in Basic Info step
        if (formData.productType !== 'auction') {
          if (!formData.price || formData.price <= 0) {
            maybeToast(t('productUpload.errors.validPrice'));
            return false;
          }
        }

        // Auction end time is always mandatory for auctions
        if (formData.productType === 'auction') {
          if (!formData.auctionEndTime) {
            maybeToast(t('productUpload.errors.auctionEndTime'));
            return false;
          }
          const endTime = new Date(formData.auctionEndTime);
          const now = new Date();
          if (endTime <= now) {
            maybeToast(t('productUpload.errors.auctionEndFuture'));
            return false;
          }
        }
        // Material optional – no validation here
        if (formData.stock < 0) {
          maybeToast(t('productUpload.errors.stockNegative'));
          return false;
        }
        if (!formData.tags || formData.tags.length === 0) {
          maybeToast(t('productUpload.errors.addTag'));
          return false;
        }
        if (!formData.colors || formData.colors.length === 0) {
          maybeToast(t('productUpload.errors.selectColor'));
          return false;
        }
        if (!formData.sizes || formData.sizes.length === 0) {
          maybeToast(t('productUpload.errors.selectSize'));
          return false;
        }
        return true;
        
      case 3: // Images
        if (!formData.images || formData.images.length === 0) {
          maybeToast(t('productUpload.errors.addImage'));
          return false;
        }
        return true;
        
      case 4: // Shipping
        if (shippingMode === 'shipping') {
          if (!selectedDeliveryOptionIds || selectedDeliveryOptionIds.length === 0) {
            maybeToast(t('productUpload.errors.selectShipping'));
            return false;
          }
        }
        return true;
        
      case 5: // Location
        // Check if map location is selected but not added to addresses
        if (selectedMapLocation && !selectedAddressId) {
          if (!silent) toast.error(t('productUpload.errors.addMapLocationFirst'), {
            duration: 5000,
            style: {
              background: '#ff6b35',
              color: '#fff',
              fontSize: '14px',
              fontWeight: 'bold',
            },
            icon: '📍',
          });
          return false;
        }
        
        // Check if no location is selected at all
        if (!selectedAddressId && !selectedMapLocation) {
          maybeToast(t('productUpload.errors.selectLocation'));
          return false;
        }
        
        return true;

      case 6: // Boost & Visibility
        // Optional step
        return true;

      case 7: // Review & Publish
        // No additional validation needed for review step
        return true;
        
      default:
        return true;
    }
  };

  const renderBoostAndVisibility = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {promotionOptionsLoading ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={22} />
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Loading boost options…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait a moment.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : promotionOptionsError ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900}>
              Boost & Visibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Couldn’t load boost options from the server.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {promotionOptionsError}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={loadPromotionOptions}>
                Retry
              </Button>
              <Button variant="outlined" onClick={() => setPromotionOptionsError(null)}>
                Dismiss
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : promotionOptions.length > 0 ? (
        <ListingPromotionSelector
          options={promotionOptions}
          value={watch('promotionAddons') || []}
          onChange={(next) => setValue('promotionAddons', next)}
          title="Boost visibility"
          subtitle="Optional add-ons to get more exposure. You can skip this step and publish normally."
        />
      ) : (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900}>
              Boost & Visibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              No boost options configured yet. An admin can add them in Admin Dashboard → Boost & Visibility.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={loadPromotionOptions}>
                Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );

  const handleNext = async () => {
    if (!validateCurrentStep(activeStep)) return;

    // If advancing from product type step, ensure a WIP product exists
    if (activeStep === 0) {
      await ensureWipDraft({ silent: true });
    }

    // If advancing from Basic Info step, persist current form values to WIP product
    if (activeStep === 1) {
      try {
        const pid = wipProductId || (await ensureWipDraft({ silent: true }));
        if (pid) {
          const f = getValues();
          const updatePayload: any = {
            name: f.name,
            description: f.description,
            brand: f.brand || null,
            condition: f.condition || null,
            size_system: f.sizeSystem || null,
            sizes: Array.isArray(f.sizes) ? f.sizes : [],
            tags: Array.isArray(f.tags) ? f.tags : [],
            colors: Array.isArray(f.colors) ? f.colors : [],
            custom_colors: Array.isArray(f.customColors) ? f.customColors : [],
            images: Array.isArray(f.images) ? f.images : [],
            backblaze_images: Array.isArray(f.backblaze_images) ? f.backblaze_images : [],
            is_job: !!f.isJob,
            product_type: f.productType === 'auction' ? 'a' : (f.productType === 'promotion' ? 'p' : 'd'),
            is_negotiable: f.saleModel === 'negotiable',
            // Price fields only when relevant (direct-buy step)
            price: f.productType !== 'auction' ? (typeof f.price === 'number' ? f.price : null) : null,
            original_price: f.productType !== 'auction' ? (typeof f.originalPrice === 'number' ? f.originalPrice : null) : null,
            // New categories array
            categories: Array.isArray(f.categories) ? f.categories : [],
          };
          const { error } = await supabase
            .from('products')
            .update(updatePayload)
            .eq('id', pid);
          if (error) {
            console.error('Failed to update WIP product (basic info):', error, updatePayload);
            toast.error(tx('productUpload.toast.saveBasicFailed', 'Failed to save basic info'));
          } else {
            if (!wipProductId) setWipProductId(pid);
            toast.success(tx('productUpload.toast.saveBasicSuccess', 'Basic info saved'));
          }
        }
      } catch (e) {
        console.error('Error saving basic info:', e);
        toast.error(tx('productUpload.toast.saveBasicFailed', 'Failed to save basic info'));
      }
    }

    // If advancing from Shipping step, persist shipping selections
    if (activeStep === 4) {
      try {
        const pid = wipProductId || (await ensureWipDraft({ silent: true }));
        if (pid) {
          if (shippingMode === 'pickup') {
            // Pickup only: clear delivery options on product and join table
            const { error: updErr } = await supabase
              .from('products')
              .update({ delivery_option_ids: null })
              .eq('id', pid);
            if (updErr) throw updErr;

            // Remove any previously linked options
            const { error: delErr } = await supabase
              .from('product_delivery_options')
              .delete()
              .eq('product_id', pid);
            if (delErr) throw delErr;

            toast.success(tx('productUpload.toast.saveShippingPickupSuccess', 'Pickup-only saved'));
          } else {
            const optionIds = Array.isArray(selectedDeliveryOptionIds) ? selectedDeliveryOptionIds : [];
            const { error: updErr } = await supabase
              .from('products')
              .update({ delivery_option_ids: optionIds })
              .eq('id', pid);
            if (updErr) throw updErr;

            // Replace join rows
            await supabase
              .from('product_delivery_options')
              .delete()
              .eq('product_id', pid);
            await DeliveryOptionsService.saveProductSelections(pid, optionIds);

            toast.success(tx('productUpload.toast.saveShippingSuccess', 'Shipping options saved'));
          }
        }
      } catch (e) {
        console.error('Error saving shipping selections:', e);
        toast.error(tx('productUpload.toast.saveShippingFailed', 'Failed to save shipping selections'));
      }
    }

    // Mark current step as completed and advance
    setCompletedSteps(prev => {
      const newCompleted = [...prev];
      if (!newCompleted.includes(activeStep)) {
        newCompleted.push(activeStep);
      }
      try { localStorage.setItem(k(COMPLETED_STEPS_KEY), JSON.stringify(newCompleted)); } catch {}
      return newCompleted;
    });

    setActiveStep((prevActiveStep) => {
      const next = prevActiveStep + 1;
      try { localStorage.setItem(k(STEP_STORAGE_KEY), String(next)); } catch {}
      return next;
    });
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => {
      const prev = Math.max(0, prevActiveStep - 1);
      try { localStorage.setItem(k(STEP_STORAGE_KEY), String(prev)); } catch {}
      return prev;
    });
  };

  // Reset wizard and fields to initial state without creating a new listing
  const handleStartFromScratch = async () => {
    const defaults: ProductFormData = {
      productType: 'direct-buy',
      saleModel: 'fixed',
      quantityType: 'single',
      name: '',
      description: '',
      category: '',
      subcategory: '',
      brand: '',
      price: 0,
      originalPrice: undefined,
      condition: 'new',
      conditionQuality: 5,
      material: '',
      stock: 0,
      tags: [],
      colors: [],
      customColors: [],
      sizeSystem: '',
      sizes: [],
      isActive: true,
      isJob: false,
      images: [],
      backblaze_images: [],
      categories: [],
      model: '',
      sku: '',
      weight: undefined,
      dimensions: undefined,
      auctionId: undefined,
      auctionStartTime: undefined,
      auctionEndTime: undefined,
      auctionReservePrice: undefined,
      auctionBidIncrement: 1.0,
    };
    reset(defaults);
    setCompletedSteps([]);
    setActiveStep(0);
    setMaxVisitedStep(0);
    setLastEditedStep(0);
    setSelectedDeliveryOptionIds([]);
    setSelectedAddressId(null);
    setSelectedMapLocation(null);
    try {
      localStorage.removeItem(FORM_STORAGE_KEY);
      localStorage.removeItem(STEP_STORAGE_KEY);
      localStorage.removeItem(SHIPPING_STORAGE_KEY);
      localStorage.removeItem(COMPLETED_STEPS_KEY);
      localStorage.removeItem('selectedAddressId');
      localStorage.removeItem('selectedMapLocation');
    } catch {}
    await ensureWipDraft({ silent: false });
    toast.success(t('productUpload.toast.resetToFirstStep') || 'Reset to first step.');
  };

  // Handle step click - validate current step before allowing navigation to any other step
  const handleStepClick = (step: number) => {
    const stepNames = [
      t('productUpload.steps.productType'),
      t('productUpload.steps.basicInfo'),
      t('productUpload.steps.details'),
      t('productUpload.steps.images'),
      t('productUpload.steps.shipping'),
      t('productUpload.steps.location'),
      t('productUpload.steps.reviewPublish')
    ];
    
    // If clicking on the same step, show notification
    if (step === activeStep) {
      toast(`${t('productUpload.toast.alreadyOnStep')} ${step + 1}: ${stepNames[step]}`, { 
        icon: 'ℹ️',
        style: {
          background: '#2196F3',
          color: '#fff',
        }
      });
      return;
    }
    
    // If clicking on a previous step (lower step number), allow navigation without validation
    if (step < activeStep) {
      toast.success(`${t('productUpload.toast.navigatingToStep')} ${step + 1}: ${stepNames[step]} (${t('productUpload.common.previousStep')})`);
      setActiveStep(step);
      try { localStorage.setItem(STEP_STORAGE_KEY, String(step)); } catch {}
      return;
    }
    
    // For any future step (higher step number), ALWAYS validate the current step first
    if (step > activeStep) {
      // Validate the current step first using the same logic as handleNext
      if (validateCurrentStep(activeStep)) {
        // Current step is valid, mark it as completed (same logic as handleNext)
        setCompletedSteps(prev => {
          const newCompleted = [...prev];
          if (!newCompleted.includes(activeStep)) {
            newCompleted.push(activeStep);
          }
          try { localStorage.setItem(COMPLETED_STEPS_KEY, JSON.stringify(newCompleted)); } catch {}
          return newCompleted;
        });
        
        // Allow navigation to the requested step
        toast.success(`${t('productUpload.toast.stepCompleted')} ${activeStep + 1}. ${t('productUpload.toast.navigatingToStep')} ${step + 1}: ${stepNames[step]}`);
        setActiveStep(step);
        setMaxVisitedStep((prev) => Math.max(prev, step));
        try { localStorage.setItem(STEP_STORAGE_KEY, String(step)); } catch {}
      }
      // If validation fails, validateCurrentStep will show the appropriate error message
      // and we don't need to do anything else - the validation function handles the error display
      return;
    }
  };

  const handleAddImage = async () => {
    if (!imageInput.trim()) return;

    setIsUploading(true);
    try {
      // Simulate image validation and upload
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demo purposes, we'll just add the URL
      const currentImages = watchedImages || [];
      setValue('images', [...currentImages, imageInput.trim()]);
      setImageInput('');
      toast.success('Image added successfully!');
    } catch (error) {
      toast.error('Failed to add image. Please check the URL.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Process each selected file
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }

      // Open crop modal for each file
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataURL = e.target?.result as string;
        if (dataURL) {
          setCropImage(dataURL);
          setCrop(undefined);
          setCompletedCrop(undefined);
          setScale(1);
          setRotate(0);
          imageProcessedRef.current = false;
          setCropModalOpen(true);
        }
      };
      reader.readAsDataURL(file);
    }
    
    // Reset the file input
    if (fileInputRef) {
      fileInputRef.value = '';
    }
  };

  const handleUploadFromDevice = () => {
    if (fileInputRef) {
      fileInputRef.click();
    }
  };

  // Camera capture functions
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      setVideoStream(stream);
      if (videoRef) {
        videoRef.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Could not access camera. Please check permissions and try again.');
      setCameraModalOpen(false);
    }
  };

  const stopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
      if (videoRef) {
        videoRef.srcObject = null;
      }
    }
  };

  const switchCamera = async () => {
    stopCamera();
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    // Will restart camera in useEffect when facingMode changes
  };

  const capturePhoto = () => {
    if (!videoRef) return;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.videoWidth;
    canvas.height = videoRef.videoHeight;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Flip the image if using front camera
      if (facingMode === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      
      ctx.drawImage(videoRef, 0, 0);
      
      canvas.toBlob((blob) => {
        if (blob) {
          // Convert blob to file
          const file = new File([blob], `camera-photo-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          
          // Process the captured image like a regular file upload
          const reader = new FileReader();
          reader.onload = (e) => {
            const dataURL = e.target?.result as string;
            if (dataURL) {
              setCropImage(dataURL);
              setCrop(undefined);
              setCompletedCrop(undefined);
              setScale(1);
              setRotate(0);
              imageProcessedRef.current = false;
              stopCamera();
              setCameraModalOpen(false);
              setCropModalOpen(true);
            }
          };
          reader.readAsDataURL(file);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  // Start/restart camera when modal opens or facing mode changes
  useEffect(() => {
    if (cameraModalOpen) {
      startCamera();
    } else {
      stopCamera();
    }
    
    // Cleanup on unmount or when modal closes
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraModalOpen, facingMode]);

  // Update video element when ref or stream changes
  useEffect(() => {
    if (videoRef && videoStream && cameraModalOpen) {
      videoRef.srcObject = videoStream;
      videoRef.play().catch(error => {
        console.error('Error playing video:', error);
      });
    }
  }, [videoRef, videoStream, cameraModalOpen]);

  const handleDocumentSelect = (document: any) => {
    // Check if the document is an image
    if (document.file_type && document.file_type.startsWith('image/')) {
      // Add the document URL to the images array
      const currentImages = watchedImages || [];
      if (document.url && !currentImages.includes(document.url)) {
        setValue('images', [...currentImages, document.url]);
        toast.success('Image added from documents!');
      } else {
        toast.error('This image is already added or URL is not available');
      }
    } else {
      toast.error('Please select an image file from your documents');
    }
    setDocumentsModalOpen(false);
  };

  const autofillStep1 = () => {
    if (categories.length === 0) {
      toast.error('Categories not loaded yet. Please wait and try again.');
      return;
    }

    const sampleProducts = [
      {
        name: 'Premium Cotton T-Shirt',
        description: 'Soft, comfortable cotton t-shirt perfect for everyday wear. Made from 100% organic cotton with a modern fit. Features a classic crew neckline and short sleeves. Ideal for casual outings, workouts, or layering.',
        brand: 'EcoWear'
      },
      {
        name: 'Designer Denim Jeans',
        description: 'Classic blue jeans with a modern slim fit. Made from premium denim with just the right amount of stretch for comfort. Features a mid-rise waist, five-pocket design, and a tapered leg. Perfect for casual and semi-formal occasions.',
        brand: 'DenimCo'
      },
      {
        name: 'Elegant Summer Dress',
        description: 'Beautiful floral summer dress perfect for warm weather. Lightweight and breathable fabric with a flattering A-line silhouette. Features a V-neckline, short sleeves, and a midi length. Perfect for brunch, garden parties, or vacation.',
        brand: 'SummerStyle'
      },
      {
        name: 'Professional Blazer',
        description: 'Classic navy blazer perfect for business meetings and formal occasions. Made from high-quality wool blend with a tailored fit. Features notched lapels, two-button closure, and structured shoulders. A timeless piece for any professional wardrobe.',
        brand: 'ProfessionalWear'
      },
      {
        name: 'Leather Crossbody Bag',
        description: 'Elegant leather crossbody bag perfect for everyday use. Made from genuine leather with a soft, supple texture. Features multiple compartments, adjustable strap, and secure closure. Perfect for carrying essentials while keeping hands free.',
        brand: 'LeatherLux'
      }
    ];

    const randomProduct = sampleProducts[Math.floor(Math.random() * sampleProducts.length)];
    setValue('name', randomProduct.name);
    setValue('description', randomProduct.description);
    // Category must be chosen manually via MegaMenu before proceeding
    setValue('category', '');
    setValue('subcategory', '');
    setValue('categories', []);
    setValue('brand', randomProduct.brand);
    
    toast.success('Step 1 autofilled with sample data!');
  };

  const autofillStep2 = () => {
    const samplePrices = [29.99, 49.99, 79.99, 89.99, 129.99, 149.99, 199.99];
    const sampleMaterials = ['Cotton', 'Polyester', 'Wool', 'Leather', 'Denim', 'Silk', 'Linen'];
    const sampleTags = [
      ['casual', 'comfortable', 'cotton'],
      ['formal', 'professional', 'business'],
      ['summer', 'lightweight', 'breathable'],
      ['vintage', 'retro', 'classic'],
      ['modern', 'trendy', 'fashionable'],
      ['eco-friendly', 'sustainable', 'organic'],
      ['luxury', 'premium', 'high-quality']
    ];
    
    const randomPrice = samplePrices[Math.floor(Math.random() * samplePrices.length)];
    const randomOriginalPrice = randomPrice * (1.2 + Math.random() * 0.3); // 20-50% higher
    const randomMaterial = sampleMaterials[Math.floor(Math.random() * sampleMaterials.length)];
    const randomTags = sampleTags[Math.floor(Math.random() * sampleTags.length)];
    const randomStock = Math.floor(Math.random() * 50) + 10; // 10-60 items
    
    setValue('price', randomPrice);
    setValue('originalPrice', Math.round(randomOriginalPrice * 100) / 100);
    setValue('material', randomMaterial);
    setValue('stock', randomStock);
    setValue('tags', randomTags);
    
    // Add some colors and sizes
    const colors = productColors.default;
    const sizeSystemKeys = Object.keys(sizeSystems);
    const randomSizeSystem = sizeSystemKeys[Math.floor(Math.random() * sizeSystemKeys.length)];
    const selectedSizeSystem = sizeSystems[randomSizeSystem as keyof typeof sizeSystems];
    
    const randomColors = colors.slice(0, Math.floor(Math.random() * 3) + 2); // 2-4 colors
    const randomSizes = selectedSizeSystem.sizes.slice(0, Math.floor(Math.random() * 4) + 3); // 3-6 sizes
    
    setValue('colors', randomColors);
    setValue('sizeSystem', randomSizeSystem);
    setValue('sizes', randomSizes);
    
    toast.success('Step 2 autofilled with sample data!');
  };

  const autofillStep3 = () => {
    const sampleImages = [
      'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1542272604-787c3835535d?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1594938298605-cd64d190e6bc?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1503341504253-dff4815485f1?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400&h=400&fit=crop',
      'https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=400&fit=crop'
    ];
    
    // Add 2-4 random images
    const numImages = Math.floor(Math.random() * 3) + 2;
    const selectedImages: string[] = [];
    
    for (let i = 0; i < numImages; i++) {
      const randomImage = sampleImages[Math.floor(Math.random() * sampleImages.length)];
      if (!selectedImages.includes(randomImage)) {
        selectedImages.push(randomImage);
      }
    }
    
    setValue('images', selectedImages);
    toast.success(`Step 3 autofilled with ${selectedImages.length} sample images!`);
  };

  const handleRemoveImage = (index: number) => {
    const currentImages = watchedImages || [];
    const url = currentImages[index];
    // Best-effort: if this preview image belongs to a Backblaze item, delete it from B2 as well
    if (b2Enabled && url) {
      try {
        // Normalize for comparison (ignore query string)
        const noQuery = url.split('?')[0];
        // Find matching B2 item by exact url, by url-without-query, or by key suffix
        const match = b2Items.find((it) => {
          const itNoQuery = (it.url || '').split('?')[0];
          if (it.url === url || itNoQuery === noQuery) return true;
          // Also try matching by key appearing at the end of URL (encoded or raw)
          const encKey = encodeURIComponent(it.key).replace(/%2F/g, '/');
          return noQuery.endsWith('/' + it.key) || noQuery.endsWith('/' + encKey);
        });
        if (match) {
          // Fire and forget; server validates user scope
          // Deliberately not awaiting to keep UI snappy; internal function handles state cleanup
          void deleteBackblazeImage(match.key);
        }
      } catch {}
    }
    setValue('images', currentImages.filter((_, i) => i !== index));
    // Also remove from backblaze_images if present
    const currentB2 = (getValues('backblaze_images') || []) as string[];
    if (currentB2 && currentB2.length) {
      const toRemove = url;
      const noQuery = (toRemove || '').split('?')[0];
      const nextB2 = currentB2.filter((u) => {
        const uNoQuery = (u || '').split('?')[0];
        return u !== toRemove && uNoQuery !== noQuery;
      });
      if (nextB2.length !== currentB2.length) setValue('backblaze_images', nextB2);
    }
  };

  const handleAddTag = (tag: string) => {
    if (tag.trim() && !watchedTags.includes(tag.trim())) {
      setValue('tags', [...watchedTags, tag.trim()]);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setValue('tags', watchedTags.filter(tag => tag !== tagToRemove));
  };

  // Color picker functions
  const handleAddCustomColor = () => {
    if (currentCustomColor && !watchedCustomColors.includes(currentCustomColor)) {
      setValue('customColors', [...watchedCustomColors, currentCustomColor]);
      setColorPickerOpen(false);
      toast.success('Custom color added!');
    }
  };

  const handleRemoveCustomColor = (colorToRemove: string) => {
    setValue('customColors', watchedCustomColors.filter(color => color !== colorToRemove));
  };

  const handleRemoveStandardColor = (colorToRemove: string) => {
    setValue('colors', watchedColors.filter(color => color !== colorToRemove));
  };

  // Helper function to get contrast color for text
  const getContrastColor = (hexColor: string) => {
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light colors, white for dark colors
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  };

  // Image cropping functions
  const onImageLoad = useCallback((img: HTMLImageElement) => {
    if (!img) return; // Add null check to prevent errors
    
    setImgRef(img);
    const { width, height } = img;
    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        1, // Free aspect ratio for product images
        width,
        height
      ),
      width,
      height
    );
    setCrop(crop);
  }, []);

  // Cleanup cropping state when modal closes
  useEffect(() => {
    if (!cropModalOpen) {
      setCropImage('');
      setCrop(undefined);
      setCompletedCrop(undefined);
      setScale(1);
      setRotate(0);
      setImgRef(null);
      imageProcessedRef.current = false;
    }
  }, [cropModalOpen]);

  // Handle image load with stable reference
  const handleImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img && img.complete && !imageProcessedRef.current) {
      imageProcessedRef.current = true;
      onImageLoad(img);
    }
  }, [onImageLoad]);

  // Handle opening image gallery
  const handleOpenGallery = useCallback((index: number) => {
    setGalleryIndex(index);
    setGalleryOpen(true);
  }, []);

  // Handle gallery index change
  const handleGalleryIndexChange = useCallback((index: number) => {
    setGalleryIndex(index);
  }, []);

  const applyCrop = async () => {
    if (!imgRef || !completedCrop) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Calculate the scale factors between the displayed image and natural image
    const scaleX = imgRef.naturalWidth / imgRef.width;
    const scaleY = imgRef.naturalHeight / imgRef.height;

    // Set canvas size to the cropped area
    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    // Enable image smoothing for better quality
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Calculate the source crop area in natural image coordinates
    const sourceX = completedCrop.x * scaleX;
    const sourceY = completedCrop.y * scaleY;
    const sourceWidth = completedCrop.width * scaleX;
    const sourceHeight = completedCrop.height * scaleY;

    // Draw the cropped portion of the image
    ctx.drawImage(
      imgRef,
      sourceX,           // Source X (crop start X in natural image)
      sourceY,           // Source Y (crop start Y in natural image)
      sourceWidth,       // Source width (crop width in natural image)
      sourceHeight,      // Source height (crop height in natural image)
      0,                 // Destination X (start at 0 in canvas)
      0,                 // Destination Y (start at 0 in canvas)
      completedCrop.width,  // Destination width (crop width)
      completedCrop.height  // Destination height (crop height)
    );

    // Convert canvas to blob and upload to storage (Supabase or Backblaze based on website-settings.json)
    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          setIsUploadingImage(true);
          
          // Get current user
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            toast.error('You must be logged in to upload images');
            setIsUploadingImage(false);
            return;
          }

          // Convert blob to file
          const file = new File([blob], `cropped-image-${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });

          // Decide upload target from website-settings.json
          let uploadTarget: 'supabase' | 'backblaze' = 'supabase';
          try {
            const settingsRes = await fetch('/website-settings.json', { cache: 'no-store' });
            const settings = settingsRes.ok ? await settingsRes.json() : null;
            if (settings && (settings.uploadTarget === 'backblaze' || settings.uploadTarget === 'supabase')) {
              uploadTarget = settings.uploadTarget;
            }
          } catch {}

          if (uploadTarget === 'backblaze') {
            // Upload via PHP to Backblaze using user's JWT
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) {
              toast.error('Missing session token');
              setIsUploadingImage(false);
              return;
            }
            const fd = new FormData();
            fd.append('file', file);
            // Include product id to create subfolder product-images/{userId}/{productId}
            if (wipProductId) {
              try { fd.append('productId', wipProductId); } catch {}
            }
            const resp = await fetch('/api/b2-upload.php', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: fd,
            });
            const raw = await resp.text();
            let json: any = null; try { json = raw ? JSON.parse(raw) : null; } catch {}
            if (!resp.ok || !json?.ok || !json?.publicUrl) {
              const detail = json?.error || raw || 'No response body';
              throw new Error(`Backblaze upload failed: ${resp.status} ${resp.statusText} - ${detail}`);
            }
            const publicUrl: string = json.publicUrl;
            const signedUrl: string | undefined = json.signedUrl;
            // For UI preview, prefer signed URL (works for private buckets)
            const addPreview = signedUrl || publicUrl;
            const currentImages: string[] = getValues('images') || [];
            const mergedPreviews = Array.from(new Set([...currentImages, addPreview]));
            setValue('images', mergedPreviews);
            // Persist canonical URL(s) for DB
            const currentB2: string[] = (getValues('backblaze_images') || []);
            const mergedB2 = Array.from(new Set([...currentB2, publicUrl]));
            setValue('backblaze_images', mergedB2);
            // Also persist immediately to the product row if we have a WIP id
            try {
              if (wipProductId) {
                await ProductService.updateProduct(wipProductId, { backblaze_images: mergedB2 });
              }
            } catch (e) {
              console.error('Failed to persist backblaze_images to product', e);
            }
          } else {
            // Upload to Supabase Storage
            const fileName = `${user.id}/${Date.now()}_${Math.random().toString(36).substring(2)}.jpg`;
            const { data, error } = await supabase.storage
              .from('product-images')
              .upload(fileName, file, {
                cacheControl: '3600',
                upsert: false
              });
            if (error) {
              console.error('Error uploading cropped image:', error);
              toast.error('Failed to upload cropped image');
              setIsUploadingImage(false);
              return;
            }
            const { data: urlData } = supabase.storage
              .from('product-images')
              .getPublicUrl(fileName);
            const currentImages = watchedImages || [];
            setValue('images', [...currentImages, urlData.publicUrl]);
          }

          setCropModalOpen(false);
          setIsUploadingImage(false);
          toast.success('Image cropped and uploaded successfully!');
        } catch (error) {
          console.error('Error in applyCrop:', error);
          toast.error('Failed to process cropped image');
          setIsUploadingImage(false);
        }
      }
    }, 'image/jpeg', 0.9);
  };

  const onSubmit = async (data: ProductFormData) => {
    // Manual validation
    if (!data.productType) {
      toast.error('Please select a product type (Direct Buy or Auction)');
      return;
    }
    if (!data.name || data.name.length < 3) {
      toast.error('Product name must be at least 3 characters');
      return;
    }
    if (!data.description || data.description.length < 10) {
      toast.error('Description must be at least 10 characters');
      return;
    }
    if (!data.category) {
      toast.error('Please select a category');
      return;
    }
    if (!data.brand) {
      toast.error('Please enter a brand');
      return;
    }
    if (!data.price || data.price <= 0) {
      const priceLabel = data.productType === 'auction' ? 'minimum bid price' : 'price';
      toast.error(`Please enter a valid ${priceLabel}`);
      return;
    }
    if (data.productType !== 'auction' && data.productType !== 'negotiable' && showMaterialSection && !data.material) {
      toast.error('Please select a material');
      return;
    }
    if (data.stock < 0) {
      toast.error('Stock cannot be negative');
      return;
    }
    if (!data.tags || data.tags.length === 0) {
      toast.error('Please add at least one tag');
      return;
    }
    if ((!data.colors || data.colors.length === 0) && (!data.customColors || data.customColors.length === 0)) {
      toast.error('Please select at least one color (standard or custom)');
      return;
    }
    if (!data.sizeSystem) {
      toast.error('Please select a size system');
      return;
    }
    if (!data.sizes || data.sizes.length === 0) {
      toast.error('Please select at least one size');
      return;
    }
    if (!data.images || data.images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    // Auction-specific validation
    if (data.productType === 'auction') {
      if (!data.auctionBidIncrement || data.auctionBidIncrement <= 0) {
        toast.error('Please enter a valid bid increment amount');
        return;
      }
      if (!data.auctionEndTime) {
        toast.error('Please select an auction end date and time');
        return;
      }
      // Check if auction end time is in the future
      const endTime = new Date(data.auctionEndTime);
      const now = new Date();
      if (endTime <= now) {
        toast.error('Auction end time must be in the future');
        return;
      }
      // Check if reserve price is higher than starting price (if provided)
      if (data.auctionReservePrice && data.auctionReservePrice <= data.price) {
        showStepToast({
          id: 'product-upload:reserve-price',
          message: 'Reserve price must be higher than starting price',
          step: 1,
          label: tx('productUpload.toast.jumpToBasicInfo', 'Jump back to Basic information to fix it'),
        });
        return;
      }
    }

    // Check content filters before proceeding
    try {
      const filterChecks = await FilterSettingsService.checkMultipleContentViolations([
        { type: 'name', value: data.name },
        { type: 'description', value: data.description },
        { type: 'brand', value: data.brand },
        { type: 'tags', value: data.tags.join(' ') },
      ]);

      if (filterChecks.violated) {
        // Find blocking filters
        const blockingFilters = filterChecks.violations
          .flatMap(v => v.violations)
          .filter(f => f.action_type === 'block');

        if (blockingFilters.length > 0) {
          const blockedFields = filterChecks.violations
            .filter(v => v.violations.some(f => f.action_type === 'block'))
            .map(v => v.type);
          
          toast.error(
            `Content blocked: Your ${blockedFields.join(', ')} contains prohibited content. Please review and modify your content.`,
            { duration: 6000 }
          );
          return;
        }

        // If only flagging filters matched, show warning but allow
        const flaggedFields = filterChecks.violations.map(v => v.type).join(', ');
        toast(
          `Content flagged for review: Your ${flaggedFields} may require admin review.`,
          { 
            duration: 5000,
            icon: '⚠️',
            style: {
              background: '#ff9800',
              color: '#fff',
            },
          }
        );
      }
    } catch (error) {
      console.error('Error checking content filters:', error);
      // Continue with upload if filter check fails (fail open)
    }

    setIsUploading(true);
    try {
      // Convert price to base price if user was in customer mode
      let finalPrice = data.price;
      let finalOriginalPrice = data.originalPrice;
      
      if (platformFeeSettings && pricingMode === 'customer' && data.productType !== 'auction') {
        // Convert customer price to base price
        finalPrice = calculateBasePriceFromCustomerPrice(data.price, platformFeeSettings);
        if (finalPrice <= 0) {
          toast.error('Price is too low to cover platform fees. Please increase the price.');
          setIsUploading(false);
          return;
        }
        // Also convert original price if provided
        if (data.originalPrice) {
          finalOriginalPrice = calculateBasePriceFromCustomerPrice(data.originalPrice, platformFeeSettings);
          if (finalOriginalPrice <= 0) {
            finalOriginalPrice = undefined; // Invalid original price, remove it
          }
        }
      }
      
      const quantityType = (data.quantityType || watchedQuantityType || 'single') as 'single' | 'multiple';
      const stockQuantity = quantityType === 'single' ? 1 : Math.max(0, Number(data.stock ?? 0));

      // Prepare product data
      const productData: CreateProductData = {
        name: data.name,
        description: data.description,
        price: finalPrice,
        original_price: finalOriginalPrice || undefined,
        stock_quantity: stockQuantity,
        // Legacy category fields removed in favor of categories[]
        categories: (getValues('categories') as string[] | undefined) || [],
        // Activation & status based on "Sofort veröffentlichen" toggle
        is_active: !!data.isActive,
        product_status: (!!data.isActive) ? 'ACTIVE' : 'INACTIVE',
        images: data.images, // May include Supabase or Backblaze URLs for UI
        backblaze_images: (watch('backblaze_images') as string[] | undefined) || [],
        colors: data.colors || [],
        custom_colors: data.customColors || [],
        size_system: data.sizeSystem,
        sizes: data.sizes || [],
        condition: data.condition as any || 'new',
        brand: data.brand || undefined,
        model: data.model || undefined,
        sku: data.sku || undefined,
        weight: data.weight || undefined,
        dimensions: data.dimensions ? {
          length: data.dimensions.length,
          width: data.dimensions.width,
          height: data.dimensions.height,
        } : undefined,
        tags: [...(data.tags || []), ...(Array.isArray(data.promotionAddons) ? data.promotionAddons.map((x) => `boost:${x}`) : [])],
        delivery_option_ids: selectedDeliveryOptionIds,
        is_job: data.isJob || false,
        // New DB fields
        product_type: (data.productType === 'auction') ? 'a' : (data.productType === 'promotion' ? 'p' : 'd'),
        is_negotiable: data.productType === 'negotiable' || data.saleModel === 'negotiable',
      };

      const selectedBoostIds = Array.isArray(data.promotionAddons) ? data.promotionAddons : [];
      const boostOptionById = new Map(
        (resolvedPromotionOptions?.options || []).map((o) => [String(o.id || ''), o] as const)
      );
      const boost = selectedBoostIds
        .filter((id) => !!String(id || '').trim())
        .map((id) => {
          const opt = boostOptionById.get(String(id));
          return {
            id: String(id),
            title: opt?.title,
            description: opt?.description,
            price: typeof opt?.price === 'number' ? opt?.price : undefined,
            currency: resolvedPromotionOptions?.currency,
            badge: opt?.badge,
            recommended: !!opt?.recommended,
            action: opt?.action,
          };
        });

      (productData as any).boost = boost;

      // If a WIP product exists, update it; otherwise insert a new product
      let product;
      if (wipProductId) {
        const updates: UpdateProductData = {
          name: productData.name,
          description: productData.description,
          price: productData.price,
          original_price: productData.original_price,
          stock_quantity: productData.stock_quantity,
          // Legacy category fields removed
          categories: productData.categories || [],
          images: productData.images,
          backblaze_images: productData.backblaze_images,
          colors: productData.colors,
          custom_colors: productData.custom_colors,
          size_system: productData.size_system,
          sizes: productData.sizes,
          condition: productData.condition,
          brand: productData.brand,
          model: productData.model,
          sku: productData.sku,
          weight: productData.weight,
          dimensions: productData.dimensions || undefined,
          tags: productData.tags || [],
          boost: (productData as any).boost,
          delivery_option_ids: productData.delivery_option_ids || [],
          is_job: productData.is_job || false,
          location_name: productData.location_name,
          latitude: productData.latitude,
          longitude: productData.longitude,
          // Set final activation & status at publish time
          is_active: !!data.isActive,
          product_status: (!!data.isActive) ? 'ACTIVE' : 'INACTIVE',
          // New DB fields
          product_type: productData.product_type,
          is_negotiable: productData.is_negotiable,
        };
        product = await ProductService.updateProduct(wipProductId, updates);
      } else {
        product = await ProductService.createProductDirect(productData);
      }
      
      console.log('Product saved:', product);
      try { sessionStorage.removeItem('currentWipProductId'); } catch {}

      try {
        const { data: auth } = await supabase.auth.getUser();
        const u = auth?.user;
        if (u?.email) {
          let profile: any = null;
          try {
            const { data: p } = await supabase
              .from('users')
              .select('first_name,last_name,alias')
              .eq('id', u.id)
              .maybeSingle();
            profile = p;
          } catch {
            profile = null;
          }

          void fetch('/api/notifications-dispatch.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              event: 'product_uploaded',
              user: {
                id: u.id,
                email: u.email,
                firstName: profile?.first_name || '',
                lastName: profile?.last_name || '',
                alias: profile?.alias || '',
              },
              product: {
                id: (product as any)?.id || '',
                name: (product as any)?.name || '',
                price: (product as any)?.price ?? '',
              },
            }),
          }).catch((e) => {
            console.warn('[ProductUploadPage] notifications-dispatch.php failed', e);
            return null;
          });
        }
      } catch {
        // best-effort
      }

      // If product type is auction, create auction entry
      if (data.productType === 'auction') {
        try {
          const auctionData: CreateAuctionData = {
            product_id: product.id,
            end_time: data.auctionEndTime!,
            starting_price: data.price,
            reserve_price: data.auctionReservePrice || undefined,
            bid_increment: data.auctionBidIncrement || 1.00,
            status: 'active', // Start the auction immediately
          };

          const auction = await AuctionService.createAuction(auctionData);
          console.log('Auction created:', auction);

          // Update the product with the auction_id
          const updatedProduct = await ProductService.updateProduct(product.id, {
            auction_id: auction.id
          });
          console.log('Product updated with auction_id:', updatedProduct);
          
          toast.success('Auction created successfully! Your product is now live for bidding.');
          
        } catch (auctionError) {
          console.error('Failed to create auction:', auctionError);
          toast.error('Product created but failed to create auction. Please contact support.');
          // Continue with success flow even if auction creation fails
        }
      }

      // Save selected delivery options
      try {
        await DeliveryOptionsService.saveProductSelections(product.id, selectedDeliveryOptionIds);
      } catch (e) {
        console.error('Failed to save delivery options for product:', e);
        // non-fatal
      }

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData?.session?.access_token || '';
        if (token) {
          void fetch('/api/store-product.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ product }),
          }).catch((e) => {
            console.warn('[ProductUploadPage] store-product.php failed', e);
            return null;
          });
        }
      } catch {
        // best-effort
      }

      setUploadedProduct(product);

      try {
        const selectedIds = Array.isArray(data.promotionAddons) ? data.promotionAddons : [];
        const currency = (resolvedPromotionOptions?.currency || 'EUR').trim() || 'EUR';
        const boostOptionById = new Map(
          (resolvedPromotionOptions?.options || []).map((o) => [String(o.id || ''), o] as const)
        );
        const items = selectedIds
          .filter((id) => !!String(id || '').trim())
          .map((id) => {
            const opt = boostOptionById.get(String(id));
            return {
              id: String(id),
              title: opt?.title,
              description: opt?.description,
              price: typeof opt?.price === 'number' ? opt?.price : undefined,
              currency: resolvedPromotionOptions?.currency,
              badge: opt?.badge,
              recommended: !!opt?.recommended,
              action: opt?.action,
            };
          });
        const totalAmount = items.reduce((sum, it) => sum + (Number(it.price) || 0), 0);
        await PlatformPurchasesService.create({
          productId: (product as any)?.id || null,
          currency,
          totalAmount,
          items,
          status: 'succeeded',
          provider: 'paypal',
        });
      } catch (e) {
        console.warn('[ProductUploadPage] Failed to write platform_purchases record (non-fatal):', e);
      }

      setShowSuccessModal(true);
      
      // Auto-close success modal after 3 seconds and navigate
      setTimeout(() => {
        setShowSuccessModal(false);
        // Check if user came from company dashboard and preserve context
        const companyParam = searchParams.get('company');
        const nameParam = searchParams.get('name');
        if (companyParam && nameParam) {
          navigate(`/dashboard?company=${companyParam}&name=${nameParam}`);
        } else {
          navigate('/dashboard');
        }
      }, 3000);
      // Clear persisted state after success
      try {
        localStorage.removeItem(FORM_STORAGE_KEY);
        localStorage.removeItem(STEP_STORAGE_KEY);
        localStorage.removeItem(SHIPPING_STORAGE_KEY);
      } catch {}
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to upload product. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };


  const renderProductType = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          gutterBottom
          sx={(t) => ({
            color: t.palette.text.primary,
            mb: 2,
          })}
        >
          {t('productUpload.sellingMethod.title')}
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          {t('productUpload.sellingMethod.subtitle')}
        </Typography>
      </Box>

      <Controller
        name="productType"
        control={control}
        rules={{ required: t('productUpload.errors.selectProductType') as string }}
        render={({ field }) => (
          <Box
            sx={{
              display: 'grid',
              gap: 3,
              width: '100%',
              alignItems: 'stretch',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(260px, 1fr))',
                lg: 'repeat(4, minmax(220px, 1fr))',
              },
            }}
          >
            {/* Direct Buy Option (only if any direct-related option is enabled) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', height: '100%' }}
            >
              {websiteSettingsLoaded && (productOptionsEnabled.fixed_single || productOptionsEnabled.fixed_multiple) && (
              <Card
                sx={(t) => ({
                  cursor: 'pointer',
                  p: { xs: 2, sm: 4 },
                  borderRadius: 3,
                  border: `2px solid ${field.value === 'direct-buy' ? t.palette.primary.main : t.palette.divider}`,
                  backgroundColor: field.value === 'direct-buy' ? t.palette.primary.main + '0A' : t.palette.background.paper,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: `2px solid ${t.palette.primary.main}`,
                    boxShadow: `0 12px 30px ${t.palette.primary.main}22`,
                  },
                  position: 'relative',
                  overflow: 'hidden',
                })}
                onClick={() => {
                  field.onChange('direct-buy');
                  setValue('saleModel', 'fixed');
                  if (!getValues('quantityType')) setValue('quantityType', 'single');
                }}
              >
                {field.value === 'direct-buy' && (
                  <Box
                    sx={(t) => ({
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: t.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    })}
                  >
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}
                
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={(t) => ({
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: '50%',
                      backgroundColor: t.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 1.5, sm: 3 },
                      boxShadow: `0 14px 34px ${t.palette.primary.main}30`,
                    })}
                  >
                    <DirectBuyIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {t('productUpload.sellingMethod.direct.title')}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 3 }, lineHeight: 1.6, display: { xs: 'none', sm: 'block' } }}>
                    {t('productUpload.sellingMethod.direct.desc')}
                  </Typography>
                  
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.direct.bullets.instantSales')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.direct.bullets.fixedPricing')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.direct.bullets.predictableRevenue')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.direct.bullets.inventoryManagement')}</Typography>
                    </Box>
                  </Box>

                  {/* Sub-options moved into secondary panel below */}
                </Box>
              </Card>
              )}
            </motion.div>

            {/* Negotiable Option (Verhandlungsbasis) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', height: '100%' }}
            >
              {websiteSettingsLoaded && (productOptionsEnabled.negotiable_single || productOptionsEnabled.negotiable_multiple) && (
              <Card
                sx={(t) => ({
                  cursor: 'pointer',
                  p: { xs: 2, sm: 4 },
                  borderRadius: 3,
                  border: `2px solid ${field.value === 'negotiable' ? t.palette.secondary.main : t.palette.divider}`,
                  background: field.value === 'negotiable'
                    ? `linear-gradient(135deg, ${t.palette.secondary.main}12 0%, ${t.palette.background.paper} 70%)`
                    : t.palette.background.paper,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: `2px solid ${t.palette.secondary.main}`,
                    boxShadow: `0 12px 30px ${t.palette.secondary.main}22`,
                  },
                  position: 'relative',
                  overflow: 'hidden',
                })}
                onClick={() => {
                  field.onChange('negotiable');
                  setValue('saleModel', 'negotiable');
                  if (!getValues('quantityType')) setValue('quantityType', 'single');
                }}
              >
                {field.value === 'negotiable' && (
                  <Box
                    sx={(t) => ({
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: t.palette.secondary.main,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    })}
                  >
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}

                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={(t) => ({
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 1.5, sm: 3 },
                      boxShadow: '0 14px 34px rgba(124, 58, 237, 0.30)',
                    })}
                  >
                    <NegotiableIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />
                  </Box>

                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: 'text.primary', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Verhandlungsbasis
                  </Typography>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 3 }, lineHeight: 1.6, display: { xs: 'none', sm: 'block' } }}>
                    Käufer können Angebote senden und ihr handelt den Preis direkt im Chat aus.
                  </Typography>

                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="body2">Preis verhandelbar</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="body2">Angebote im Chat</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                      <Typography variant="body2">Flexible Einigung</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
              )}
            </motion.div>

            {/* Auction Option (only if enabled) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', height: '100%' }}
            >
              {websiteSettingsLoaded && productOptionsEnabled.auction && (
              <Card
                sx={{
                  cursor: 'pointer',
                  p: { xs: 2, sm: 4 },
                  borderRadius: 3,
                  border: field.value === 'auction' ? '3px solid #f59e0b' : '2px solid #e0e0e0',
                  background: field.value === 'auction' 
                    ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)'
                    : 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '2px solid #f59e0b',
                    boxShadow: '0 8px 25px rgba(245, 158, 11, 0.15)',
                  },
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onClick={() => field.onChange('auction')}
              >
                {field.value === 'auction' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#f59e0b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}
                
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 1.5, sm: 3 },
                      boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                    }}
                  >
                    <AuctionIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#333', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {t('productUpload.sellingMethod.auction.title')}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 3 }, lineHeight: 1.6, display: { xs: 'none', sm: 'block' } }}>
                    {t('productUpload.sellingMethod.auction.desc')}
                  </Typography>
                  
                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.auction.bullets.competitiveBidding')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.auction.bullets.higherValue')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.auction.bullets.timeLimited')}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                      <Typography variant="body2">{t('productUpload.sellingMethod.auction.bullets.marketDiscovery')}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
              )}
            </motion.div>

            {/* Promotion Option (only if enabled) */}
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              style={{ width: '100%', height: '100%' }}
            >
              {websiteSettingsLoaded && productOptionsEnabled.promotion && (
              <Card
                sx={{
                  cursor: 'pointer',
                  p: { xs: 2, sm: 4 },
                  borderRadius: 3,
                  border: field.value === 'promotion' ? '3px solid #10b981' : '2px solid #e0e0e0',
                  background: field.value === 'promotion' 
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(59, 130, 246, 0.06) 100%)'
                    : 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    border: '2px solid #10b981',
                    boxShadow: '0 8px 25px rgba(16, 185, 129, 0.15)',
                  },
                  position: 'relative',
                  overflow: 'hidden',
                }}
                onClick={() => field.onChange('promotion')}
              >
                {field.value === 'promotion' && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      background: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 16, color: 'white' }} />
                  </Box>
                )}

                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: { xs: 56, sm: 80 },
                      height: { xs: 56, sm: 80 },
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: { xs: 1.5, sm: 3 },
                      boxShadow: '0 8px 25px rgba(16, 185, 129, 0.3)',
                    }}
                  >
                    <PromotionIcon sx={{ fontSize: { xs: 28, sm: 40 }, color: 'white' }} />
                  </Box>

                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#333', fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Angebot / Aktion
                  </Typography>

                  <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 1.5, sm: 3 }, lineHeight: 1.6, display: { xs: 'none', sm: 'block' } }}>
                    Starte Promotionen oder zeitlich begrenzte Angebote für dein Produkt.
                  </Typography>

                  <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', gap: 1, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography variant="body2">Mehr Sichtbarkeit</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography variant="body2">Zeitlich begrenzte Aktionen</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                      <Typography variant="body2">Flexibel kombinierbar</Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
              )}
            </motion.div>
          </Box>
        )}
      />

      {/* Secondary panel: Direct Buy option selection (still part of Step 1) */}
      {websiteSettingsLoaded && getValues('productType') === 'direct-buy' && (productOptionsEnabled.fixed_single || productOptionsEnabled.fixed_multiple) && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={(t) => ({
              mb: 2.25,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: `1px solid ${t.palette.divider}`,
              background: `linear-gradient(135deg, ${t.palette.primary.main}12 0%, ${t.palette.background.paper} 55%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box
                sx={(t) => ({
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: t.palette.action.hover,
                  border: `1px solid ${t.palette.divider}`,
                  color: t.palette.primary.main,
                  flex: '0 0 auto',
                })}
              >
                <FixedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Direktverkauf – Lagerbestand
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Wähle Einzelverkauf oder Mehrere und setze deinen Bestand.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Row 1: Festpreis */}
          {websiteSettingsLoaded && (productOptionsEnabled.fixed_single || productOptionsEnabled.fixed_multiple) && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {[ 
                  productOptionsEnabled.fixed_single ? { key: 'fixed_single', title: 'Einzelverkauf', desc: 'Ein Artikel (1 Stück)', icon: <FixedIcon /> } : null,
                  productOptionsEnabled.fixed_multiple ? { key: 'fixed_multiple', title: 'Mehrere', desc: 'Bestand verwalten', icon: <MultipleIcon /> } : null,
                ].filter(Boolean).map((o: any) => {
                  const active = watchedSaleModel === 'fixed' && watchedQuantityType === (o.key === 'fixed_single' ? 'single' : 'multiple');
                  return (
                    <motion.div key={o.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                      <Card
                        variant="outlined"
                        sx={(t) => ({
                          position: 'relative',
                          height: '100%',
                          p: 1,
                          borderRadius: 3,
                          borderWidth: 2,
                          borderColor: active ? t.palette.primary.main : t.palette.divider,
                          backgroundColor: active ? t.palette.primary.main + '0A' : t.palette.background.paper,
                          transition: 'all 0.25s ease',
                          boxShadow: active ? `0 10px 28px ${t.palette.primary.main}22` : 'none',
                          '&:hover': {
                            borderColor: t.palette.primary.main,
                            boxShadow: `0 10px 28px ${t.palette.primary.main}22`,
                          },
                        })}
                        onClick={() => {
                          setValue('productType', 'direct-buy');
                          setValue('saleModel', 'fixed');
                          setValue('quantityType', o.key === 'fixed_single' ? 'single' : 'multiple');
                        }}
                      >
                        {active && (
                          <Box
                            sx={(t) => ({
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              width: 26,
                              height: 26,
                              borderRadius: '50%',
                              bgcolor: t.palette.primary.main,
                              color: t.palette.primary.contrastText,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: `0 10px 22px ${t.palette.primary.main}40`,
                            })}
                          >
                            <CheckCircleIcon sx={{ fontSize: 16 }} />
                          </Box>
                        )}
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                            <Avatar
                              sx={(t) => ({
                                bgcolor: t.palette.action.hover,
                                color: t.palette.primary.main,
                                width: 36,
                                height: 36,
                                border: `1px solid ${t.palette.divider}`,
                              })}
                            >
                              {o.icon}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                {o.title}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {o.desc}
                              </Typography>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </Box>
            </Box>
          )}

        </Box>
      )}

      {/* Secondary panel: Negotiable option selection (VERHANDLUNGSBASIS) */}
      {websiteSettingsLoaded && getValues('productType') === 'negotiable' && (productOptionsEnabled.negotiable_single || productOptionsEnabled.negotiable_multiple) && (
        <Box sx={{ mt: 4 }}>
          <Box
            sx={(t) => ({
              mb: 2.25,
              p: { xs: 1.5, sm: 2 },
              borderRadius: 3,
              border: `1px solid ${t.palette.divider}`,
              background: `linear-gradient(135deg, rgba(124, 58, 237, 0.10) 0%, ${t.palette.background.paper} 55%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            })}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box
                sx={(t) => ({
                  width: 40,
                  height: 40,
                  borderRadius: 999,
                  display: 'grid',
                  placeItems: 'center',
                  backgroundColor: t.palette.action.hover,
                  border: `1px solid ${t.palette.divider}`,
                  color: t.palette.secondary.main,
                  flex: '0 0 auto',
                })}
              >
                <NegotiableIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                  Verhandlungsbasis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  Wähle Einzelverkauf oder Mehrere für Angebote im Chat.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {[
              productOptionsEnabled.negotiable_single ? { key: 'negotiable_single', title: 'Einzelverkauf', desc: 'Angebot für 1 Artikel', icon: <NegotiableIcon /> } : null,
              productOptionsEnabled.negotiable_multiple ? { key: 'negotiable_multiple', title: 'Mehrere', desc: 'Angebote für Bestand', icon: <NegotiableIcon /> } : null,
            ].filter(Boolean).map((o: any) => {
              const active = watchedSaleModel === 'negotiable' && watchedQuantityType === (o.key === 'negotiable_single' ? 'single' : 'multiple');
              return (
                <motion.div key={o.key} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Card
                    variant="outlined"
                    sx={(t) => ({
                      position: 'relative',
                      height: '100%',
                      p: 1,
                      borderRadius: 3,
                      borderWidth: 2,
                      borderColor: active ? t.palette.secondary.main : t.palette.divider,
                      backgroundColor: active ? t.palette.secondary.main + '0A' : t.palette.background.paper,
                      transition: 'all 0.25s ease',
                      boxShadow: active ? `0 10px 28px ${t.palette.secondary.main}22` : 'none',
                      '&:hover': {
                        borderColor: t.palette.secondary.main,
                        boxShadow: `0 10px 28px ${t.palette.secondary.main}22`,
                      },
                    })}
                    onClick={() => {
                      setValue('productType', 'negotiable');
                      setValue('saleModel', 'negotiable');
                      setValue('quantityType', o.key === 'negotiable_single' ? 'single' : 'multiple');
                    }}
                  >
                    {active && (
                      <Box
                        sx={(t) => ({
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 26,
                          height: 26,
                          borderRadius: '50%',
                          bgcolor: t.palette.secondary.main,
                          color: t.palette.secondary.contrastText,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 10px 22px ${t.palette.secondary.main}40`,
                        })}
                      >
                        <CheckCircleIcon sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar
                          sx={(t) => ({
                            bgcolor: t.palette.action.hover,
                            color: t.palette.secondary.main,
                            width: 36,
                            height: 36,
                            border: `1px solid ${t.palette.divider}`,
                          })}
                        >
                          {o.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                            {o.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {o.desc}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </Box>
        </Box>
      )}

      {errors.productType && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errors.productType.message}
        </Alert>
      )}
    </Box>
  );

  const renderBasicInfo = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('productUpload.basic.title')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={autofillStep1}
          sx={{ textTransform: 'none' }}
        >
          {t('productUpload.basic.autofill')}
        </Button>
      </Box>

      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('productUpload.basic.name.label')}
            fullWidth
            error={!!errors.name}
            helperText={errors.name?.message}
            placeholder={t('productUpload.basic.name.placeholder')}
          />
        )}
      />

      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('productUpload.basic.description.label')}
            fullWidth
            multiline
            minRows={4}
            maxRows={12}
            error={!!errors.description}
            helperText={errors.description?.message}
            placeholder={t('productUpload.basic.description.placeholder')}
            slotProps={{
              input: {
                sx: {
                  '& textarea': {
                    resize: 'vertical',
                  },
                },
              },
            }}
          />
        )}
      />

      <Controller
        name="brand"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label={t('productUpload.basic.brand.label')}
            fullWidth
            error={!!errors.brand}
            helperText={errors.brand?.message}
          />
        )}
      />

      {/* Category Selector using MegaMenu */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: hasSelectedCategory ? 'flex-start' : 'center' }}>
        <Button
          size="large"
          variant={hasSelectedCategory ? 'outlined' : 'contained'}
          color={hasSelectedCategory ? 'primary' : 'secondary'}
          endIcon={!hasSelectedCategory ? <ArrowForwardIcon /> : undefined}
          onClick={() => setCategoryPickerOpen(true)}
          sx={{
            px: 3.5,
            py: 1.25,
            fontSize: { xs: '0.95rem', sm: '1rem' },
            borderRadius: 3,
            ...(hasSelectedCategory ? {} : {
              animation: 'pulseArrow 1.8s infinite',
              '@keyframes pulseArrow': {
                '0%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0.45)' },
                '70%': { boxShadow: '0 0 0 12px rgba(124, 58, 237, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(124, 58, 237, 0)' },
              },
            })
          }}
        >
          {hasSelectedCategory
            ? tx('productUpload.basic.category.change', 'Change category')
            : tx('productUpload.basic.category.select', 'Select category')}
        </Button>
      </Box>
      {Array.isArray(watchedCategoriesArr) && watchedCategoriesArr.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {watchedCategoriesArr.map((c) => (
            <Chip
              key={c}
              label={c}
              onDelete={() => {
                const curr = (getValues('categories') || []).filter((x: string) => x !== c);
                setValue('categories', curr);
              }}
              variant="outlined"
              color="primary"
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      )}
      {categoryPickerOpen && (
        <Dialog
          open
          onClose={() => setCategoryPickerOpen(false)}
          fullScreen={isMobile}
          maxWidth="md"
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              width: isMobile ? '100vw' : { xs: '92vw', sm: 680, md: 860 },
              mx: 'auto',
              maxHeight: isMobile ? '100vh' : '80vh',
              overflow: 'hidden',
            },
          }}
        >
          <AppBar sx={{ position: 'sticky', boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', color: 'text.primary' }}>
            <Toolbar sx={{ minHeight: 48 }}>
              <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                {t('header.categories')}
              </Typography>
              <IconButton edge="end" onClick={() => setCategoryPickerOpen(false)} aria-label="close">
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: 1, overflowY: 'auto', maxHeight: isMobile ? 'calc(100vh - 48px)' : 'calc(80vh - 48px)' }}>
            {(() => {
              const isTopEnabled = (cat: string): boolean => {
                if (Object.prototype.hasOwnProperty.call(categoriesEnabled, cat) && !categoriesEnabled[cat]) return false;
                return true;
              };
              const isSubEnabled = (top: string, label: string): boolean => {
                const m = subcategoriesEnabled?.[top];
                if (!m || typeof m[label] === 'undefined') return true;
                return !!m[label];
              };
              return Object.keys(defaultMegaMenuData)
                .filter((k) => k.toLowerCase() !== 'default')
                .filter((cat) => isTopEnabled(cat))
                .map((cat) => {
                const catData = (defaultMegaMenuData as any)[cat];
                if (!catData) return null;
                return (
                  <Accordion key={cat} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTopCatIcon(cat)}
                        <Typography sx={{ fontWeight: 700 }}>{cat}</Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0.5, pb: 1.5 }}>
                      {(catData.left || [])
                        .filter((item: any) => {
                          const label = (item?.label || '').toString().toLowerCase();
                          return item?.key !== 'all' && label !== 'alle anzeigen';
                        })
                        .map((item: any) => {
                          const cols = (catData.rightByKey?.[item.key]) || [];
                          const hasChildren = cols.some((col: any) => (col.items || []).some((sub: any) => {
                            const lbl = typeof sub === 'string' ? sub : sub?.label ?? '';
                            return !!lbl && lbl.toLowerCase() !== 'alle anzeigen';
                          }));
                          if (!hasChildren) {
                            // Leaf subcategory: allow direct select without expand icon
                            const entry = `${cat} / ${item.label}`;
                            const selected = (getValues('categories') || []).includes(entry);
                            if (!isSubEnabled(cat, item.label)) return null;
                            return (
                              <ListItemButton
                                key={`${cat}-${item.key}-leaf`}
                                sx={{
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                  mb: 1,
                                  py: 1,
                                  backgroundColor: selected ? 'rgba(2,132,199,0.08)' : 'transparent',
                                }}
                                onClick={() => {
                                  setValue('category', cat);
                                  setValue('subcategory', item.label);
                                  const curr = getValues('categories') || [];
                                  if (curr.includes(entry)) {
                                    setValue('categories', curr.filter((x: string) => x !== entry));
                                  } else {
                                    setValue('categories', [...curr, entry]);
                                    toast.success(`${tx('productUpload.basic.category.selected', 'Selected')}: ${entry}`);
                                  }
                                }}
                              >
                                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600 }} />
                                {selected && (
                                  <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                                    <CheckCircleIcon fontSize="small" />
                                  </ListItemIcon>
                                )}
                              </ListItemButton>
                            );
                          }

                          const enabledCols = (cols || [])
                            .filter((col: any) => {
                              const t = (col?.title || '').toString().trim().toLowerCase();
                              // Skip the 'Alle anzeigen' section from MegaMenu
                              if (t === 'alle anzeigen') return false;
                              const enabledItems = (col.items || []).filter((sub: any) => {
                                const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                if (!label || label.toLowerCase() === 'alle anzeigen') return false;
                                return isSubEnabled(cat, label);
                              });
                              return enabledItems.length > 0;
                            })
                            .map((col: any) => ({
                              ...col,
                              items: (col.items || []).filter((sub: any) => {
                                const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                if (!label || label.toLowerCase() === 'alle anzeigen') return false;
                                return isSubEnabled(cat, label);
                              }),
                            }));

                          // If this group has no enabled children, hide the entire accordion
                          if (enabledCols.length === 0) return null;

                          return (
                            <Accordion key={`${cat}-${item.key}`} disableGutters elevation={0} sx={{ '&::before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.5 }}>
                                <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                              </AccordionSummary>
                              <AccordionDetails sx={{ pt: 0, pb: 1 }}>
                                {enabledCols.map((col: any) => (
                                  <Box key={`${cat}-${item.key}-${col.title}`} sx={{ mb: 1.25 }}>
                                    {col?.title && (
                                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700, mb: 0.5 }}>
                                        {col.title}
                                      </Typography>
                                    )}
                                    <List disablePadding>
                                      {(col.items || []).map((sub: any) => {
                                        const label = typeof sub === 'string' ? sub : sub?.label ?? '';
                                        if (!label || label.toLowerCase() === 'alle anzeigen') return null;
                                        const entry = `${cat} / ${item.label} / ${label}`;
                                        const selected = (getValues('categories') || []).includes(entry);
                                        return (
                                          <ListItemButton
                                            key={`${cat}-${item.key}-${col.title}-${label}`}
                                            sx={{
                                              borderRadius: 1,
                                              py: 0.75,
                                              backgroundColor: selected ? 'rgba(2,132,199,0.08)' : 'transparent',
                                              border: selected ? '1px solid rgba(2,132,199,0.25)' : '1px solid transparent',
                                              '&:hover': {
                                                backgroundColor: selected ? 'rgba(2,132,199,0.12)' : 'rgba(0,0,0,0.03)'
                                              }
                                            }}
                                            onClick={() => {
                                              setValue('category', cat);
                                              setValue('subcategory', `${item.label} / ${label}`);
                                              const curr = getValues('categories') || [];
                                              if (curr.includes(entry)) {
                                                setValue('categories', curr.filter((x: string) => x !== entry));
                                              } else {
                                                setValue('categories', [...curr, entry]);
                                                toast.success(`${tx('productUpload.basic.category.selected', 'Selected')}: ${entry}`);
                                              }
                                            }}
                                          >
                                            <ListItemText primary={label} primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }} />
                                            {selected && (
                                              <ListItemIcon sx={{ minWidth: 32, color: 'primary.main' }}>
                                                <CheckCircleIcon fontSize="small" />
                                              </ListItemIcon>
                                            )}
                                          </ListItemButton>
                                        );
                                      })}
                                    </List>
                                  </Box>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                    </AccordionDetails>
                  </Accordion>
                );
              });
            })()}
          </Box>
          <DialogActions sx={{ px: 2, py: 1.5 }}>
            <Button
              onClick={() => {
                setValue('categories', []);
              }}
              color="inherit"
              sx={{ textTransform: 'none' }}
            >
              {tx('common.clear', 'Clear')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() => setCategoryPickerOpen(false)}
              variant="contained"
              sx={{ textTransform: 'none' }}
            >
              {tx('common.done', 'Done')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );

  const renderDetails = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('productUpload.details.title')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={autofillStep2}
          sx={{ textTransform: 'none' }}
        >
          {t('productUpload.details.autofill')}
        </Button>
      </Box>

      {/* Price fields - different layout for auction vs direct buy */}
      {watchedProductType === 'auction' ? (
        // For auctions, no price fields here - they're all in the auction settings section
        <Box sx={{ 
          p: 3, 
          bgcolor: '#f0f9ff', 
          borderRadius: 2, 
          border: '1px solid #0ea5e9',
          textAlign: 'center'
        }}>
          <Typography variant="body1" color="text.secondary">
            {t('productUpload.details.auctionInfo')}
          </Typography>
        </Box>
      ) : (
        // For direct buy, show advanced pricing options
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Pricing Mode Toggle */}
          {platformFeeSettings && (
            <Card sx={{ 
              p: 3, 
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              borderRadius: 2
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                  {t('productUpload.details.pricingMode.title')}
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pricingMode === 'customer'}
                      onChange={(e) => {
                        const newMode = e.target.checked ? 'customer' : 'base';
                        setPricingMode(newMode);
                        // When switching modes, recalculate the price
                        if (platformFeeSettings && watchedPrice > 0) {
                          if (newMode === 'customer') {
                            // Switching to customer mode - current price is base, calculate customer price
                            const customerPrice = calculatePriceWithFeesSync(watchedPrice, platformFeeSettings);
                            setValue('price', customerPrice);
                          } else {
                            // Switching to base mode - current price is customer, calculate base price
                            const basePrice = calculateBasePriceFromCustomerPrice(watchedPrice, platformFeeSettings);
                            setValue('price', basePrice);
                          }
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {pricingMode === 'customer' ? t('productUpload.details.pricingMode.setCustomer') : t('productUpload.details.pricingMode.setBase')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                        {pricingMode === 'customer' 
                          ? t('productUpload.details.pricingMode.helpCustomer')
                          : t('productUpload.details.pricingMode.helpBase')}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {/* Fee Information Display */}
              <Box sx={{ 
                p: 2, 
                bgcolor: 'rgba(255, 255, 255, 0.7)', 
                borderRadius: 2,
                border: '1px solid rgba(102, 126, 234, 0.1)'
              }}>
                {/** Ensure watchedPrice is numeric for rendering/calculations */}
                {(() => { return null; })()}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="body2" fontWeight="bold" color="text.primary">
                    {t('productUpload.details.fees.platformFee')}
                  </Typography>
                  {platformFeeSettings.fee_type === 'fixed' ? (
                    <Chip 
                      label={`${`$${(platformFeeSettings.fee_amount?.toFixed(2) || '0.00')}`} ${t('productUpload.details.fees.fixedPerSale')}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip 
                      label={`${platformFeeSettings.fee_percentage?.toFixed(2) || '0.00'}% ${t('productUpload.details.fees.ofSalePrice')}`}
                      size="small"
                      color="primary"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
                {Number(watchedPrice) > 0 && platformFeeSettings && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                    {pricingMode === 'customer' ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('productUpload.details.fees.customerPrice')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            ${Number(watchedPrice || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        {(() => {
                          const basePrice = calculateBasePriceFromCustomerPrice(Number(watchedPrice || 0), platformFeeSettings);
                          const feeAmount = watchedPrice - basePrice;
                          return (
                            <>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('productUpload.details.fees.platformFee')}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" color="error.main">
                                  -${feeAmount.toFixed(2)}{platformFeeSettings.fee_type !== 'fixed' && ` (${Number(platformFeeSettings.fee_percentage || 0).toFixed(2)}%)`}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                <Typography variant="body2" fontWeight="bold" color="text.primary">
                                  {t('productUpload.details.fees.youWillReceive')}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="success.main">
                                  ${basePrice.toFixed(2)}
                                </Typography>
                              </Box>
                            </>
                          );
                        })()}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('productUpload.details.fees.basePrice')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            ${Number(watchedPrice || 0).toFixed(2)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('productUpload.details.fees.platformFee')}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold" color="error.main">
                            +${calculatePlatformFeeSync(Number(watchedPrice || 0), platformFeeSettings).toFixed(2)}{platformFeeSettings.fee_type !== 'fixed' && ` (${Number(platformFeeSettings.fee_percentage || 0).toFixed(2)}%)`}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                          <Typography variant="body2" fontWeight="bold" color="text.primary">
                            {t('productUpload.details.fees.customerWillPay')}
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="success.main">
                            ${calculatePriceWithFeesSync(Number(watchedPrice || 0), platformFeeSettings).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          )}

          {/* Price Input Fields */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="price"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={pricingMode === 'customer' ? t('productUpload.details.fields.customerPrice') : t('productUpload.details.fields.basePrice')}
                  type="number"
                  fullWidth
                  error={!!errors.price}
                  helperText={
                    errors.price?.message || 
                    (pricingMode === 'customer' 
                      ? t('productUpload.details.fields.customerPriceHelp')
                      : t('productUpload.details.fields.basePriceHelp'))
                  }
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || 0;
                    field.onChange(e);
                    
                    // Auto-update calculations when price changes
                    if (platformFeeSettings && pricingMode === 'customer' && value > 0) {
                      // When in customer mode, validate the price is high enough
                      const calculatedBase = calculateBasePriceFromCustomerPrice(value, platformFeeSettings);
                      if (calculatedBase <= 0) {
                        // Price too low to cover fees
                        toast.error(
                          platformFeeSettings.fee_type === 'fixed'
                            ? `Price must be at least $${(platformFeeSettings.fee_amount || 0).toFixed(2)} to cover platform fees`
                            : 'Price must be high enough to cover platform fees'
                        );
                      }
                    }
                  }}
                />
              )}
            />

            <Controller
              name="originalPrice"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('productUpload.details.fields.originalPrice')}
                  type="number"
                  fullWidth
                  helperText={t('productUpload.details.fields.originalPriceHelp')}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value) || undefined;
                    // If original price is set and pricing mode is customer, adjust it too
                    if (value && platformFeeSettings && pricingMode === 'customer') {
                      // Calculate what the original base price would be
                      const originalBasePrice = calculateBasePriceFromCustomerPrice(value, platformFeeSettings);
                      field.onChange(originalBasePrice > 0 ? value : undefined);
                    } else {
                      field.onChange(e);
                    }
                  }}
                />
              )}
            />
          </Box>

          {/* Quick Price Suggestions */}
          {platformFeeSettings && pricingMode === 'customer' && (
            <Box sx={{ 
              p: 2, 
              bgcolor: 'rgba(255, 248, 220, 0.3)', 
              borderRadius: 2,
              border: '1px solid rgba(255, 193, 7, 0.3)'
            }}>
              <Typography variant="body2" fontWeight="bold" color="text.primary" gutterBottom>
                {t('productUpload.details.quickSuggestions')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {[25, 50, 75, 100, 150, 200].map((targetPrice) => {
                  const basePrice = calculateBasePriceFromCustomerPrice(targetPrice, platformFeeSettings);
                  if (basePrice <= 0) return null;
                  return (
                    <Chip
                      key={targetPrice}
                      label={`${`$${targetPrice.toFixed(2)}` } → ${t('productUpload.details.quickSuggestionYouGet')} ${`$${basePrice.toFixed(2)}`}`}
                      onClick={() => {
                        setValue('price', targetPrice);
                      }}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white'
                        }
                      }}
                      variant="outlined"
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Auction-specific fields */}
      {watchedProductType === 'auction' && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 4, 
          p: 4, 
          bgcolor: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', 
          borderRadius: 3, 
          border: '2px solid #f59e0b',
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background pattern */}
          <Box sx={{
            position: 'absolute',
            top: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(251, 191, 36, 0.1) 100%)',
            zIndex: 0
          }} />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h5" fontWeight="bold" sx={{ 
              color: '#92400e', 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mb: 1
            }}>
              <Box sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)'
              }}>
                <AuctionIcon sx={{ fontSize: 20, color: 'white' }} />
              </Box>
              {t('productUpload.details.auction.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400e', opacity: 0.8, mb: 3 }}>
              {t('productUpload.details.auction.subtitle')}
            </Typography>
            
            {/* Minimum Bid Price - First and most important */}
            <Box sx={{ mb: 3 }}>
              <Controller
                name="price"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('productUpload.details.auction.minBid')}
                    type="number"
                    fullWidth
                    error={!!errors.price}
                    helperText={errors.price?.message || t('productUpload.details.auction.minBidHelp')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                          borderWidth: 2,
                        },
                      }
                    }}
                  />
                )}
              />
            </Box>

            {/* Reserve Price and Bid Increment */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <Controller
                name="auctionReservePrice"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('productUpload.details.auction.reserve')}
                    type="number"
                    fullWidth
                    helperText={t('productUpload.details.auction.reserveHelp')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                          borderWidth: 2,
                        },
                      }
                    }}
                  />
                )}
              />

              <Controller
                name="auctionBidIncrement"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('productUpload.details.auction.increment')}
                    type="number"
                    fullWidth
                    error={!!errors.auctionBidIncrement}
                    helperText={errors.auctionBidIncrement?.message || t('productUpload.details.auction.incrementHelp')}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#f59e0b',
                          borderWidth: 2,
                        },
                      }
                    }}
                  />
                )}
              />
            </Box>

            {/* Auction End Time */}
            <Controller
              name="auctionEndTime"
              control={control}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    {...field}
                    label={t('productUpload.details.auction.endTime')}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue: Dayjs | null) => {
                      field.onChange(newValue ? newValue.toISOString() : '');
                    }}
                    minDateTime={dayjs().add(1, 'hour')}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.auctionEndTime,
                        helperText: errors.auctionEndTime?.message || t('productUpload.details.auction.endTimeHelp'),
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#f59e0b',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#f59e0b',
                              borderWidth: 2,
                            },
                            '& input': {
                              color: '#92400e',
                              fontWeight: 500,
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: '#92400e',
                            fontWeight: 600,
                            '&.Mui-focused': {
                              color: '#f59e0b',
                            },
                          },
                          '& .MuiFormHelperText-root': {
                            color: '#92400e',
                            opacity: 0.8,
                          },
                        },
                      },
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            borderRadius: 2,
                            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                            border: '1px solid #f59e0b',
                          },
                          '& .MuiPickersCalendarHeader-root': {
                            backgroundColor: '#fef3c7',
                            '& .MuiIconButton-root': {
                              color: '#f59e0b',
                              '&:hover': {
                                backgroundColor: '#fde68a',
                              },
                            },
                            '& .MuiPickersCalendarHeader-label': {
                              color: '#92400e',
                              fontWeight: 600,
                            },
                          },
                          '& .MuiDayCalendar-root': {
                            '& .MuiPickersDay-root': {
                              '&:hover': {
                                backgroundColor: '#fef3c7',
                              },
                              '&.Mui-selected': {
                                backgroundColor: '#f59e0b',
                                '&:hover': {
                                  backgroundColor: '#d97706',
                                },
                              },
                              '&.MuiPickersDay-today': {
                                border: '1px solid #f59e0b',
                                color: '#f59e0b',
                              },
                            },
                          },
                          '& .MuiTimeClock-root': {
                            '& .MuiClock-root': {
                              '& .MuiClock-meridiemText': {
                                color: '#92400e',
                              },
                              '& .MuiClockNumber-root': {
                                color: '#92400e',
                                '&.Mui-selected': {
                                  backgroundColor: '#f59e0b',
                                  color: 'white',
                                },
                              },
                            },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Controller
          name="condition"
          control={control}
          render={({ field }) => {
            const current = field.value;
            const options = [
              { key: 'new', label: t('productUpload.details.condition.new'), Icon: FiberNewIcon, color: 'success.main' },
              { key: 'used', label: t('productUpload.details.condition.used'), Icon: HistoryIcon, color: 'warning.main' },
              { key: 'refurbished', label: t('productUpload.details.condition.refurbished'), Icon: BuildCircleIcon, color: 'info.main' },
            ];
            return (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, width: '100%' }}>
                {options.map((o) => {
                  const active = current === o.key;
                  const IconCmp = o.Icon;
                  return (
                    <Card
                      key={o.key}
                      onClick={() => {
                        field.onChange(o.key);
                        if (o.key === 'new') setNewSparkleTick((v) => v + 1);
                      }}
                      variant={active ? 'elevation' : 'outlined'}
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        borderWidth: 2,
                        cursor: 'pointer',
                        position: 'relative',
                        borderColor: active ? 'transparent' : 'divider',
                        bgcolor: active ? 'background.paper' : 'background.paper',
                        boxShadow: active ? '0 10px 28px rgba(0,0,0,0.10)' : 'none',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: active ? 'primary.light' : 'primary.main',
                          boxShadow: '0 10px 28px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      {active && (
                        <Box sx={{ position: 'absolute', top: 10, right: 10, width: 24, height: 24, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckCircleIcon sx={{ fontSize: 16 }} />
                        </Box>
                      )}
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: o.color, width: { xs: 64, sm: 72 }, height: { xs: 64, sm: 72 } }}>
                            <IconCmp sx={{ fontSize: { xs: 38, sm: 44 }, color: '#fff' }} />
                          </Avatar>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{o.label}</Typography>
                          {o.key === 'new' && (
                            <Typography variant="caption" color="text.secondary">{tx('productUpload.details.condition.newHelp', 'Brand new, unused item')}</Typography>
                          )}
                          {o.key === 'used' && (
                            <Typography variant="caption" color="text.secondary">{tx('productUpload.details.condition.usedHelp', 'Pre-owned item in usable condition')}</Typography>
                          )}
                          {o.key === 'refurbished' && (
                            <Typography variant="caption" color="text.secondary">{tx('productUpload.details.condition.refurbishedHelp', 'Professionally restored to good working order')}</Typography>
                          )}
                        </Box>
                      </CardContent>
                      {o.key === 'new' && newSparkleTick > 0 && (
                        <Box key={newSparkleTick}
                          sx={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
                        >
                          {[
                            { x: 0, y: -50, c: '#22c55e' },
                            { x: 46, y: -26, c: '#60a5fa' },
                            { x: 56, y: 12, c: '#f59e0b' },
                            { x: 0, y: 54, c: '#e879f9' },
                            { x: -56, y: 12, c: '#34d399' },
                            { x: -46, y: -26, c: '#a78bfa' },
                          ].map((p, idx) => (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0.9, scale: 0, x: 0, y: 0 }}
                              animate={{ opacity: 0, scale: 1, x: p.x, y: p.y }}
                              transition={{ duration: 0.7, ease: 'easeOut' }}
                              style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                background: p.c,
                                boxShadow: `0 0 10px ${p.c}`,
                                transform: 'translate(-50%, -50%)',
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Card>
                  );
                })}
              </Box>
            );
          }}
        />

        {watchedQuantityType === 'multiple' && (
          <Controller
            name="stock"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('productUpload.details.stock')}
                type="number"
                fullWidth
                error={!!errors.stock}
                helperText={errors.stock?.message}
              />
            )}
          />
        )}
      </Box>

      {/* Condition quality rating (only for Used / Refurbished) */}
      {(watch('condition') === 'used' || watch('condition') === 'refurbished') && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 3, background: 'linear-gradient(135deg, rgba(59,130,246,0.04) 0%, rgba(16,185,129,0.04) 100%)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <SparkleIcon sx={{ color: 'warning.main' }} />
              <Typography variant="subtitle1" fontWeight={800}>
                {tx('productUpload.details.condition.qualityTitle', 'Condition Quality')}
              </Typography>
            </Box>
            <Controller
              name="conditionQuality"
              control={control}
              render={({ field }) => {
                const value = Number(field.value || 4);
                const labelMap: Record<number, string> = {
                  1: tx('productUpload.details.condition.quality.1', 'Fair'),
                  2: tx('productUpload.details.condition.quality.2', 'Acceptable'),
                  3: tx('productUpload.details.condition.quality.3', 'Good'),
                  4: tx('productUpload.details.condition.quality.4', 'Very Good'),
                  5: tx('productUpload.details.condition.quality.5', 'Like New'),
                };
                const face = (() => {
                  switch (value) {
                    case 1: return <SentimentVeryDissatisfiedIcon sx={{ color: 'error.main' }} />;
                    case 2: return <SentimentDissatisfiedIcon sx={{ color: 'warning.main' }} />;
                    case 3: return <SentimentSatisfiedIcon sx={{ color: 'info.main' }} />;
                    case 4: return <SentimentSatisfiedAltIcon sx={{ color: 'success.main' }} />;
                    default: return <SentimentVerySatisfiedIcon sx={{ color: 'success.dark' }} />;
                  }
                })();
                return (
                  <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'center',
                    justifyContent: { xs: 'center', sm: 'space-between' },
                    gap: { xs: 1.25, sm: 1.5 }
                  }}>
                    <Rating
                      value={value}
                      max={5}
                      onChange={(_, v) => field.onChange(v || 1)}
                      icon={<StarIcon sx={{ color: 'warning.main', fontSize: { xs: 44, sm: 52 } }} />}
                      emptyIcon={<StarBorderIcon sx={{ color: 'warning.light', fontSize: { xs: 44, sm: 52 } }} />}
                    />
                    <motion.div key={value} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.22 }}>
                      <Chip
                        icon={face}
                        color="warning"
                        label={labelMap[value] || ''}
                        sx={{ fontWeight: 800, px: { xs: 1.5, sm: 1.75 }, height: { xs: 40, sm: 42 }, fontSize: { xs: '0.95rem', sm: '1rem' }, '& .MuiChip-icon': { ml: 0.5 } }}
                      />
                    </motion.div>
                  </Box>
                );
              }}
              defaultValue={4}
            />
          </Card>
        </motion.div>
      )}

      {showMaterialSection && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('productUpload.details.material')} <Typography component="span" variant="caption" color="text.secondary">({t('common.optional') || 'optional'})</Typography>
          </Typography>
          <Controller
            name="material"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.material}>
                <InputLabel>{t('productUpload.details.material')}</InputLabel>
                <Select {...field} label={t('productUpload.details.material')}>
                  {commonMaterials.map((material) => (
                    <MenuItem key={material} value={material}>
                      {material}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
        </Box>
      )}

      {/* Colors */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('productUpload.details.colors.title')}
        </Typography>
        
        {/* Standard Colors */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t('productUpload.details.colors.standard')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {watchedColors.map((color) => (
              <Chip
                key={color}
                label={color}
                onDelete={() => handleRemoveStandardColor(color)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(8, 1fr)', sm: 'repeat(12, 1fr)', md: 'repeat(16, 1fr)' },
              gap: 1,
            }}
          >
            {productColors.default.map((clr) => {
              const selected = watchedColors.includes(clr);
              return (
                <Tooltip key={clr} title={clr} arrow>
                  <motion.div whileHover={{ scale: 1.28 }} whileTap={{ scale: 0.94 }} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      onClick={() => {
                        const curr = watchedColors;
                        if (selected) {
                          setValue('colors', curr.filter((c: string) => c !== clr));
                        } else {
                          setValue('colors', [...curr, clr]);
                        }
                      }}
                      sx={{
                        width: { xs: 28, sm: 30 },
                        height: { xs: 28, sm: 30 },
                        borderRadius: '50%',
                        bgcolor: (clr.toLowerCase() === 'white') ? '#fff' : (clr.toLowerCase() === 'black' ? '#000' : undefined),
                        background: (clr.toLowerCase() !== 'white' && clr.toLowerCase() !== 'black') ? clr : undefined,
                        border: '2px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        boxShadow: selected ? '0 0 0 4px rgba(59, 130, 246, 0.28)' : 'inset 0 0 0 1px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'transform .15s ease, box-shadow .2s ease',
                        '&:hover': {
                          boxShadow: selected ? '0 0 0 6px rgba(59, 130, 246, 0.3)' : '0 6px 20px rgba(0,0,0,0.25)',
                        },
                        '&:after': {
                          content: '""',
                          position: 'absolute',
                          inset: -6,
                          borderRadius: '50%',
                          boxShadow: '0 0 0 0 rgba(99,102,241,0.35)',
                          transition: 'box-shadow .35s ease',
                        },
                        '&:hover:after': {
                          boxShadow: '0 0 0 10px rgba(99,102,241,0.15)'
                        }
                      }}
                    >
                      {selected && (
                        <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 16, background: 'white', borderRadius: '50%' }} />
                      )}
                    </Box>
                  </motion.div>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Size System Selection */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('productUpload.details.sizes.title')}
        </Typography>
        <Controller
          name="sizeSystem"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.sizeSystem}>
              <InputLabel>{t('productUpload.details.sizes.selectSystem')}</InputLabel>
              <Select
                {...field}
                label={t('productUpload.details.sizes.selectSystem')}
                onChange={(e) => {
                  field.onChange(e);
                  setValue('sizes', []); // Reset sizes when system changes
                }}
              >
                {Object.entries(sizeSystems)
                  .filter(([key]) => allowedSizeSystemKeys.includes(key))
                  .map(([key, system]) => (
                  <MenuItem key={key} value={key}>
                    {system.name}
                  </MenuItem>
                ))}
              </Select>
              {hasSelectedCategory && inferredSizeSystem && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, ml: 0.5, display: 'block' }}>
                  {tx('productUpload.details.sizes.lockedByCategory', 'Size system is auto-selected based on category.')}
                </Typography>
              )}
              {errors.sizeSystem && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {errors.sizeSystem.message}
                </Typography>
              )}
            </FormControl>
          )}
        />
      </Box>

      {/* Sizes */}
      {watchedSizeSystem && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('productUpload.details.sizes.available')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {watchedSizes.map((size) => (
              <Chip
                key={size}
                label={size}
                onDelete={() => {
                  setValue('sizes', watchedSizes.filter(s => s !== size));
                }}
              />
            ))}
          </Box>
          <FormControl fullWidth>
            <InputLabel>{t('productUpload.details.sizes.add')}</InputLabel>
            <Select
              value=""
              onChange={(e) => {
                if (e.target.value && !watchedSizes.includes(e.target.value)) {
                  setValue('sizes', [...watchedSizes, e.target.value]);
                }
              }}
              label={t('productUpload.details.sizes.add')}
            >
              {(sizeSystems[watchedSizeSystem as keyof typeof sizeSystems]?.sizes || []).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Tags */}
      <Box>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          {t('productUpload.details.tags.title')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {watchedTags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              onDelete={() => handleRemoveTag(tag)}
              color="default"
              variant="outlined"
            />
          ))}
        </Box>
        <TextField
          label={t('productUpload.details.tags.add')}
          fullWidth
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const input = e.target as HTMLInputElement;
              handleAddTag(input.value);
              input.value = '';
            }
          }}
          placeholder={t('productUpload.details.tags.placeholder')}
        />
      </Box>
    </Box>
  );

  const renderImages = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight="bold">
          {t('productUpload.images.title')}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={autofillStep3}
          sx={{ textTransform: 'none' }}
        >
          {t('productUpload.images.autofill')}
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('productUpload.images.info.line1')} 
        {t('productUpload.images.info.line2')}
      </Alert>

      {/* Image Upload */}
      <Paper sx={{ p: 3, border: '2px dashed', borderColor: 'primary.main', bgcolor: 'primary.50' }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            {t('productUpload.images.addTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('productUpload.images.addSubtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label={t('productUpload.images.urlLabel')}
            fullWidth
            value={imageInput}
            onChange={(e) => setImageInput(e.target.value)}
            placeholder={t('productUpload.images.urlPlaceholder')}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddImage}
            disabled={!imageInput.trim() || isUploading}
            startIcon={<AddIcon />}
          >
            {t('productUpload.images.addImageBtn')}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            fullWidth
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
            startIcon={<CameraIcon />}
            onClick={handleUploadFromDevice}
            disabled={isUploading}
          >
            {isUploading ? t('productUpload.images.uploading') : t('productUpload.images.uploadFromDevice')}
          </Button>
          <Button
            variant="contained"
            fullWidth
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
            startIcon={<CameraIcon />}
            onClick={() => setCameraModalOpen(true)}
            disabled={isUploading}
            color="primary"
          >
            {t('productUpload.images.takePhoto')}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' } }}
            startIcon={<DocumentIcon />}
            onClick={() => setDocumentsModalOpen(true)}
            disabled={isUploading}
          >
            {t('productUpload.images.selectFromDocuments')}
          </Button>
        </Box>

        {/* Hidden file input */}
        <input
          type="file"
          ref={setFileInputRef}
          onChange={handleFileUpload}
          accept="image/*"
          multiple
          style={{ display: 'none' }}
        />
      </Paper>

      {/* Removed separate Backblaze grid to avoid duplicate visual preview. All previews are shown below. */}

      {/* Image Preview */}
      {watchedImages.length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {`${t('productUpload.images.previewTitle')} (${watchedImages.length})`}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {watchedImages.map((image, index) => (
              <Box 
                key={index} 
                sx={{ 
                  position: 'relative',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
                onClick={() => handleOpenGallery(index)}
              >
                <Box
                  component="img"
                  src={image}
                  alt={`${t('productUpload.images.altPrefix')} ${index + 1}`}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'grey.300',
                  }}
                  onError={() => handleImageError(image)}
                />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage(index);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
                {/* Click to view overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    borderRadius: 2,
                    '&:hover': {
                      opacity: 1,
                    },
                  }}
                >
                  <PreviewIcon sx={{ color: 'white', fontSize: 32 }} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );

  // Shipping step state
  const [deliveryOptions, setDeliveryOptions] = useState<DeliveryOption[]>([]);
  const [selectedDeliveryOptionIds, setSelectedDeliveryOptionIds] = useState<string[]>([]);
  const [deliveryOptionsLoading, setDeliveryOptionsLoading] = useState(false);
  const [deliveryOptionsError, setDeliveryOptionsError] = useState<string | null>(null);
  const [selectedCarrier, setSelectedCarrier] = useState<'DHL' | 'Hermes' | 'DPD'>('DHL');
  const [shippingMode, setShippingMode] = useState<'pickup' | 'shipping'>('shipping');

  // Location step state
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(null);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState<string | null>(null);
  
  // Map popup state
  const [mapPopupOpen, setMapPopupOpen] = useState(false);
  const [selectedAddressForMap, setSelectedAddressForMap] = useState<Address | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedMapLocation, setSelectedMapLocation] = useState<any>(null);
  
  // Settings modal state
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Load cached map location on component mount
  useEffect(() => {
    const cachedMapLocation = localStorage.getItem('selectedMapLocation');
    if (cachedMapLocation) {
      try {
        const parsedData = JSON.parse(cachedMapLocation);
        const { location, timestamp } = parsedData;
        
        // Check if cache is not older than 24 hours
        const cacheAge = Date.now() - timestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
        
        if (cacheAge < maxAge) {
          setSelectedMapLocation(location);
          console.log('📍 Loaded cached map location:', location);
        } else {
          console.log('🗑️ Cached map location expired, removing...');
          localStorage.removeItem('selectedMapLocation');
        }
      } catch (error) {
        console.error('Error parsing cached map location:', error);
        localStorage.removeItem('selectedMapLocation');
      }
    }
  }, []);

  // Save map location to localStorage whenever it changes
  useEffect(() => {
    if (selectedMapLocation) {
      const cacheData = {
        location: selectedMapLocation,
        timestamp: Date.now()
      };
      localStorage.setItem('selectedMapLocation', JSON.stringify(cacheData));
      console.log('💾 Saved map location to cache:', selectedMapLocation);
    } else {
      localStorage.removeItem('selectedMapLocation');
      console.log('🗑️ Removed map location from cache');
    }
  }, [selectedMapLocation]);

  useEffect(() => {
    const load = async () => {
      try {
        setDeliveryOptionsLoading(true);
        setDeliveryOptionsError(null);
        const options = await DeliveryOptionsService.getAll('DE');
        setDeliveryOptions(options);
      } catch (e: any) {
        setDeliveryOptionsError(e?.message || 'Failed to load delivery options');
      } finally {
        setDeliveryOptionsLoading(false);
      }
    };
    load();
  }, []);

  // Load addresses for location step
  useEffect(() => {
    const loadAddresses = async () => {
      try {
        setAddressesLoading(true);
        setAddressesError(null);
        const userAddresses = await AddressService.getUserAddresses();
        setAddresses(userAddresses);
        
        // Set default address if available
        const defaultAddress = userAddresses.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        }
      } catch (e: any) {
        setAddressesError(e?.message || 'Failed to load addresses');
      } finally {
        setAddressesLoading(false);
      }
    };
    loadAddresses();
  }, []);

  // Persist shipping selections
  useEffect(() => {
    try { localStorage.setItem(k(SHIPPING_STORAGE_KEY), JSON.stringify(selectedDeliveryOptionIds)); } catch {}
  }, [selectedDeliveryOptionIds]);

  // Persist shipping mode
  useEffect(() => {
    try { localStorage.setItem(k(SHIPPING_MODE_KEY), shippingMode); } catch {}
  }, [shippingMode]);

  // If user switches to pickup, clear any previously selected delivery options
  useEffect(() => {
    if (shippingMode === 'pickup') {
      setSelectedDeliveryOptionIds([]);
      try { localStorage.setItem(k(SHIPPING_STORAGE_KEY), JSON.stringify([])); } catch {}
    }
  }, [shippingMode]);

  // Persist location selection
  useEffect(() => {
    try { localStorage.setItem('selectedAddressId', selectedAddressId || ''); } catch {}
  }, [selectedAddressId]);

  // Persist step
  useEffect(() => {
    try { localStorage.setItem(k(STEP_STORAGE_KEY), String(activeStep)); } catch {}
  }, [activeStep]);

  // Persist form data on every change
  useEffect(() => {
    const subscription = watch((value) => {
      try { localStorage.setItem(k(FORM_STORAGE_KEY), JSON.stringify(value)); } catch {}
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const renderShipping = () => {
    const grouped: Record<string, DeliveryOption[]> = deliveryOptions.reduce((acc, opt) => {
      (acc[opt.carrier] = acc[opt.carrier] || []).push(opt);
      return acc;
    }, {} as Record<string, DeliveryOption[]>);

    const current = (grouped[selectedCarrier] || []).sort((a, b) => (a.price_eur || 0) - (b.price_eur || 0));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Shipping mode selector */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Paper
            onClick={() => {
              setShippingMode('pickup');
              setSelectedDeliveryOptionIds([]);
              try { localStorage.setItem(k(SHIPPING_STORAGE_KEY), JSON.stringify([])); } catch {}
            }}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: 2,
              borderColor: shippingMode === 'pickup' ? 'primary.main' : 'divider',
              bgcolor: shippingMode === 'pickup' ? 'primary.50' : 'background.paper',
              borderRadius: 2,
              transition: 'all .2s ease',
              '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">Nur Abholung</Typography>
              {shippingMode === 'pickup' && <Chip color="primary" label="Ausgewählt" size="small" />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Käufer holen den Artikel vor Ort ab. Es werden keine Versandoptionen angezeigt.
            </Typography>
          </Paper>
          <Paper
            onClick={() => setShippingMode('shipping')}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: 2,
              borderColor: shippingMode === 'shipping' ? 'primary.main' : 'divider',
              bgcolor: shippingMode === 'shipping' ? 'primary.50' : 'background.paper',
              borderRadius: 2,
              transition: 'all .2s ease',
              '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="bold">Versand verfügbar</Typography>
              {shippingMode === 'shipping' && <Chip color="primary" label="Ausgewählt" size="small" />}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Wähle einen Versanddienstleister und passende Tarife aus.
            </Typography>
          </Paper>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {t('productUpload.shipping.title')}
          </Typography>
          {shippingMode === 'shipping' && selectedDeliveryOptionIds.length > 0 && (
            <Chip color="success" label={`${selectedDeliveryOptionIds.length} ${t('productUpload.shipping.selectedSuffix')}`} />
          )}
        </Box>
        {deliveryOptionsError && (
          <Alert severity="error">{deliveryOptionsError}</Alert>
        )}

        {shippingMode === 'pickup' ? (
          <Alert severity="info">Nur Abholung ausgewählt – Versand wird für dieses Produkt deaktiviert.</Alert>
        ) : (
        <Paper sx={{ p: 2 }}>
          <Tabs
            value={selectedCarrier}
            onChange={(_, v) => setSelectedCarrier(v)}
            textColor="primary"
            indicatorColor="primary"
            sx={{ mb: 2 }}
          >
            <Tab value="DHL" label={`DHL${(grouped['DHL']?.length ? ` (${grouped['DHL'].length})` : '')}`} />
            <Tab value="Hermes" label={`Hermes${(grouped['Hermes']?.length ? ` (${grouped['Hermes'].length})` : '')}`} />
            <Tab value="DPD" label={`DPD${(grouped['DPD']?.length ? ` (${grouped['DPD'].length})` : '')}`} />
          </Tabs>

          {deliveryOptionsLoading ? (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <CircularProgress size={20} />
              <Typography>{t('productUpload.shipping.loading')}</Typography>
            </Box>
          ) : current.length === 0 ? (
            <Typography color="text.secondary">{`${t('productUpload.shipping.noneForCarrierPrefix')} ${selectedCarrier}.`}</Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 2,
              }}
            >
              {current.map((opt) => {
                const selected = selectedDeliveryOptionIds.includes(opt.id);
                return (
                  <Paper
                    key={opt.id}
                    onClick={() => {
                      setSelectedDeliveryOptionIds((prev) =>
                        selected ? prev.filter((id) => id !== opt.id) : Array.from(new Set([...prev, opt.id]))
                      );
                    }}
                    sx={{
                      p: 2,
                      border: '2px solid',
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.50' : 'background.paper',
                      cursor: 'pointer',
                      borderRadius: 2,
                      transition: 'all .2s ease',
                      '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {opt.name}
                      </Typography>
                      <Chip color={selected ? 'primary' : 'default'} label={opt.price_eur != null ? `€${opt.price_eur.toFixed(2)}` : '—'} size="small" />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {selectedCarrier} • {opt.max_weight_kg ? `${opt.max_weight_kg} kg ${t('productUpload.shipping.card.max')}` : t('productUpload.shipping.card.weightLimitsVary')}
                    </Typography>
                    {(opt.max_length_cm || opt.max_width_cm || opt.max_height_cm) && (
                      <Typography variant="caption" color="text.secondary">
                        {`${t('productUpload.shipping.card.sizeUpTo')} ${opt.max_length_cm || '-'}×${opt.max_width_cm || '-'}×${opt.max_height_cm || '-'} ${t('productUpload.shipping.card.cm')}`}
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          )}
        </Paper>
        )}

        {shippingMode === 'shipping' && selectedDeliveryOptionIds.length > 0 && (
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>{t('productUpload.shipping.selectedTitle')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {deliveryOptions
                .filter((o) => selectedDeliveryOptionIds.includes(o.id))
                .map((o) => (
                  <Chip
                    key={o.id}
                    label={`${o.carrier} – ${o.name}`}
                    onDelete={() => setSelectedDeliveryOptionIds((prev) => prev.filter((id) => id !== o.id))}
                  />
                ))}
            </Box>
          </Paper>
        )}

        {shippingMode === 'shipping' && (
          <Alert severity="info">{t('productUpload.shipping.helper')}</Alert>
        )}
      </Box>
    );
  };

  const renderLocation = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {t('productUpload.location.title')}
          </Typography>
          {selectedAddressId && (
            <Chip color="success" label={t('productUpload.location.badge.selected')} />
          )}
          {!selectedAddressId && selectedMapLocation && (
            <Chip color="primary" label={t('productUpload.location.badge.mapSelected')} />
          )}
        </Box>
        
        <Alert severity="info">{t('productUpload.location.info')}</Alert>

        {addressesError && (
          <Alert severity="error">{addressesError}</Alert>
        )}

        {addressesLoading ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <CircularProgress size={20} />
            <Typography>{t('productUpload.location.loading')}</Typography>
          </Box>
        ) : addresses.length === 0 ? (
          <Alert 
            severity="warning" 
            sx={{ 
              borderRadius: 2,
              '& .MuiAlert-message': {
                width: '100%'
              }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('productUpload.location.empty.title')}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('productUpload.location.empty.subtitle')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<LocationIcon />}
                onClick={() => setSettingsOpen(true)}
                sx={{
                  backgroundColor: '#667eea',
                  color: 'white',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  '&:hover': {
                    backgroundColor: '#5a6fd8',
                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                  alignSelf: 'flex-start',
                }}
              >
                {t('productUpload.location.empty.action')}
              </Button>
            </Box>
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 3,
            mt: 2
          }}>
            {addresses.map((address) => (
              <Paper
                key={address.id}
                onClick={() => {
                  setSelectedAddressId(address.id);
                  // Keep map location data but don't clear it
                }}
                sx={{
                  p: 3,
                  cursor: 'pointer',
                  border: selectedAddressId === address.id ? 2 : 1,
                  borderColor: selectedAddressId === address.id ? 'primary.main' : 'divider',
                  backgroundColor: selectedAddressId === address.id ? 'primary.50' : 'background.paper',
                  borderRadius: 3,
                  boxShadow: selectedAddressId === address.id ? 3 : 1,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: selectedAddressId === address.id ? 'primary.100' : 'grey.50',
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                  '&::before': selectedAddressId === address.id ? {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                  } : {},
                }}
              >
                {/* Header with title and action buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" sx={{ 
                      color: selectedAddressId === address.id ? 'primary.main' : 'text.primary',
                      mb: 1
                    }}>
                      {address.title}
                    </Typography>
                    {address.is_default && (
                      <Chip 
                        label={t('productUpload.location.default')}
                        color="primary" 
                        size="small" 
                        sx={{ 
                          fontSize: '0.75rem',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }} 
                      />
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Map Icon Button */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAddressForMap(address);
                        setMapPopupOpen(true);
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: 'primary.50',
                        color: 'primary.main',
                        border: '1px solid',
                        borderColor: 'primary.200',
                        '&:hover': {
                          backgroundColor: 'primary.100',
                          borderColor: 'primary.300',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      title={t('productUpload.location.viewOnMap')}
                    >
                      <MapIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    
                    {/* Selection Indicator */}
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: selectedAddressId === address.id ? 'primary.main' : 'transparent',
                      border: selectedAddressId === address.id ? 'none' : '2px solid',
                      borderColor: selectedAddressId === address.id ? 'primary.main' : 'grey.400',
                      transition: 'all 0.2s ease-in-out',
                    }}>
                      {selectedAddressId === address.id && (
                        <Box sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: 'white',
                        }} />
                      )}
                    </Box>
                  </Box>
                </Box>
                
                {/* Address content */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {/* Name and company */}
                  <Box>
                    <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                      {address.full_name}
                    </Typography>
                    {address.company && (
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        fontStyle: 'italic',
                        fontSize: '0.875rem'
                      }}>
                        {address.company}
                      </Typography>
                    )}
                  </Box>
                  
                  {/* Address lines */}
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: selectedAddressId === address.id ? 'primary.25' : 'grey.50',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: selectedAddressId === address.id ? 'primary.200' : 'grey.200',
                  }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {address.address_line_1}
                    </Typography>
                    {address.address_line_2 && (
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {address.address_line_2}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {address.postal_code} {address.city}
                    </Typography>
                    <Typography variant="body2">
                      {address.state}, {address.country}
                    </Typography>
                  </Box>
                  
                  {/* Phone number */}
                  {address.phone && (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 1,
                      p: 1.5,
                      backgroundColor: 'grey.50',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200',
                    }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: 'primary.100',
                        color: 'primary.main',
                        fontSize: '0.875rem',
                      }}>
                        📞
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {address.phone}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {selectedAddressId && !selectedMapLocation && (
          <Paper sx={{ p: 2, backgroundColor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
            <Typography variant="subtitle2" gutterBottom color="success.dark">
              {t('productUpload.location.selectedTitle')}
            </Typography>
            {(() => {
              const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
              return selectedAddress ? (
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {selectedAddress.title} - {selectedAddress.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAddress.address_line_1}, {selectedAddress.postal_code} {selectedAddress.city}, {selectedAddress.country}
                  </Typography>
                </Box>
              ) : null;
            })()}
          </Paper>
        )}

        {/* Map Selected Location Tile - Always visible when map location exists */}
        {selectedMapLocation && (
          <Paper
            onClick={() => {
              // Select map location and clear address selection
              setSelectedAddressId(null);
            }}
            sx={{
              p: 3,
              cursor: 'pointer',
              border: !selectedAddressId ? 2 : 1,
              borderColor: !selectedAddressId ? 'primary.main' : 'divider',
              backgroundColor: !selectedAddressId ? 'primary.50' : 'background.paper',
              borderRadius: 3,
              boxShadow: !selectedAddressId ? 3 : 1,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: !selectedAddressId ? 'primary.100' : 'grey.50',
                boxShadow: 4,
                transform: 'translateY(-2px)',
              },
              '&::before': !selectedAddressId ? {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
              } : {},
            }}
          >
            {/* Header with title and action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="bold" sx={{ 
                  color: 'primary.main',
                  mb: 1
                }}>
                  {t('productUpload.location.mapSelected.title')}
                </Typography>
                <Chip 
                  label={t('productUpload.location.mapSelected.badge')} 
                  color="primary" 
                  size="small" 
                  sx={{ 
                    fontSize: '0.75rem',
                    height: 24,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }} 
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* Map Icon Button */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // Set the map location data for the popup without triggering search
                    setSelectedAddressForMap({
                      id: 'map-location',
                      title: 'Map Location',
                      full_name: selectedMapLocation.name,
                      company: '',
                      address_line_1: selectedMapLocation.street || '',
                      address_line_2: '',
                      city: selectedMapLocation.city || '',
                      state: selectedMapLocation.state || '',
                      postal_code: selectedMapLocation.postalCode || '',
                      country: selectedMapLocation.country || '',
                      phone: '',
                      is_default: false,
                      is_active: true,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                      user_id: ''
                    });
                    setMapPopupOpen(true);
                  }}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'primary.50',
                    color: 'primary.main',
                    border: '1px solid',
                    borderColor: 'primary.200',
                    '&:hover': {
                      backgroundColor: 'primary.100',
                      borderColor: 'primary.300',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  title={t('productUpload.location.viewOnMap')}
                >
                  <MapIcon sx={{ fontSize: 16 }} />
                </IconButton>
                
                {/* Selection Indicator */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: !selectedAddressId ? 'primary.main' : 'transparent',
                  border: !selectedAddressId ? 'none' : '2px solid',
                  borderColor: !selectedAddressId ? 'primary.main' : 'grey.400',
                  transition: 'all 0.2s ease-in-out',
                }}>
                  {!selectedAddressId && (
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                    }} />
                  )}
                </Box>
              </Box>
            </Box>
            
            {/* Address content */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {/* Location Name */}
              <Box>
                <Typography variant="body1" fontWeight="medium" sx={{ mb: 0.5 }}>
                  {selectedMapLocation.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ 
                  fontStyle: 'italic',
                  fontSize: '0.875rem'
                }}>
                  {t('productUpload.location.mapSelected.subtitle')}
                </Typography>
              </Box>
              
              {/* Address lines */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: 'primary.25',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'primary.200',
              }}>
                {selectedMapLocation.street && selectedMapLocation.houseNumber && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {selectedMapLocation.street} {selectedMapLocation.houseNumber}
                  </Typography>
                )}
                {selectedMapLocation.city && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {selectedMapLocation.city}
                  </Typography>
                )}
                {selectedMapLocation.state && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {selectedMapLocation.state}
                  </Typography>
                )}
                {selectedMapLocation.country && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {selectedMapLocation.country}
                  </Typography>
                )}
                {selectedMapLocation.postalCode && (
                  <Typography variant="body2">
                    {selectedMapLocation.postalCode}
                  </Typography>
                )}
              </Box>
              
              {/* Coordinates */}
              {(selectedMapLocation.lat !== 0 || selectedMapLocation.lng !== 0) && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 1,
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: 1,
                }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                    📐 {t('productUpload.location.coordinates')}
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'monospace' }}>
                    {selectedMapLocation.lat.toFixed(6)}, {selectedMapLocation.lng.toFixed(6)}
                  </Typography>
                </Box>
              )}
              
              {/* Add to Addresses Button */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setSettingsOpen(true)}
                sx={{
                  alignSelf: 'flex-start',
                  borderColor: hasMapLocationNotAdded ? '#ff6b35' : 'primary.main',
                  color: hasMapLocationNotAdded ? '#ff6b35' : 'primary.main',
                  backgroundColor: hasMapLocationNotAdded ? '#fff5f2' : 'transparent',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  borderWidth: hasMapLocationNotAdded ? 2 : 1,
                  '&:hover': {
                    backgroundColor: hasMapLocationNotAdded ? '#ff6b35' : 'primary.50',
                    borderColor: hasMapLocationNotAdded ? '#e55a2b' : 'primary.dark',
                    color: hasMapLocationNotAdded ? 'white' : 'primary.dark',
                    transform: 'translateY(-1px)',
                    boxShadow: hasMapLocationNotAdded 
                      ? '0 4px 12px rgba(255, 107, 53, 0.3)' 
                      : '0 4px 12px rgba(25, 118, 210, 0.15)',
                  },
                  transition: 'all 0.2s ease-in-out',
                  animation: hasMapLocationNotAdded ? 'pulse 2s infinite' : 'none',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(255, 107, 53, 0.4)' },
                    '70%': { boxShadow: '0 0 0 10px rgba(255, 107, 53, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(255, 107, 53, 0)' },
                  },
                }}
              >
                {hasMapLocationNotAdded ? t('productUpload.location.addToAddressesRequired') : t('productUpload.location.addToAddresses')}
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    );
  };

  // Handle location selection from map
  const handleLocationSelect = (location: any) => {
    console.log('Location selected from map:', location);
    
    // Store the selected location data for display
    setSelectedMapLocation(location);
    
    // Clear any selected address when map location is selected
    setSelectedAddressId(null);
    
    setMapPopupOpen(false);
  };

  // Handle new address added from settings modal
  const handleAddressAdded = (newAddress: Address) => {
    console.log('New address added:', newAddress);
    
    // Add the new address to the addresses list
    setAddresses(prevAddresses => [...prevAddresses, newAddress]);
    
    // Optionally select the newly added address
    setSelectedAddressId(newAddress.id);
    
    // Clear the map location since it's now added to addresses
    setSelectedMapLocation(null);
    
    // Close the settings modal
    setSettingsOpen(false);
    
    toast.success('Address added and selected!');
  };

  // Check if user has map location selected but not added to addresses
  const hasMapLocationNotAdded = selectedMapLocation && !selectedAddressId;

  // Function to clear cached map location
  const clearMapLocationCache = () => {
    setSelectedMapLocation(null);
    localStorage.removeItem('selectedMapLocation');
    console.log('🗑️ Cleared map location cache');
  };

  const renderReview = () => {
    const formData = watch();

    const selectedBoostIds = Array.isArray(formData.promotionAddons) ? formData.promotionAddons : [];
    const currency = (
      (resolvedPromotionOptions?.currency || promotionOptions?.[0]?.currency || 'EUR')
    ).trim() || 'EUR';

    const boostOptionById = new Map(
      (resolvedPromotionOptions?.options || []).map((o) => [String(o.id || ''), o] as const)
    );
    const boostUiById = new Map((promotionOptions || []).map((o) => [String(o.id), o] as const));
    const selectedBoost = selectedBoostIds
      .filter((id) => !!String(id || '').trim())
      .map((id) => {
        const opt = boostOptionById.get(String(id));
        const uiOpt = boostUiById.get(String(id));
        return {
          id: String(id),
          title: opt?.title ?? uiOpt?.title,
          description: opt?.description ?? uiOpt?.description,
          price:
            typeof opt?.price === 'number'
              ? opt?.price
              : (typeof uiOpt?.price === 'number' ? uiOpt.price : 0),
          currency,
          badge: opt?.badge ?? uiOpt?.badge,
          recommended: !!(opt?.recommended ?? uiOpt?.recommended),
          action: opt?.action,
        };
      });
    const boostTotal = selectedBoost.reduce((sum, it) => sum + (Number(it.price) || 0), 0);
    
    // Get selected delivery options
    const selectedDeliveryOptions = deliveryOptions.filter(option => 
      selectedDeliveryOptionIds.includes(option.id)
    );
    
    // Get category and subcategory names
    const selectedCategory = categories.find(cat => cat.id === formData.category);
    const selectedSubcategory = subcategories.find(sub => sub.id === formData.subcategory);
    const sizeSystem = sizeSystems[formData.sizeSystem as keyof typeof sizeSystems];
    
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {t('productUpload.steps.reviewPublish')}
        </Typography>

        {/* Product Type Section */}
        <Card sx={{ p: 3, borderRadius: 2, border: '2px solid', borderColor: formData.productType === 'auction' ? '#f59e0b' : '#667eea' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              width: 40, 
              height: 40, 
              borderRadius: '50%', 
              background: formData.productType === 'auction' 
                ? 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {formData.productType === 'auction' ? (
                <AuctionIcon sx={{ fontSize: 20, color: 'white' }} />
              ) : (
                <DirectBuyIcon sx={{ fontSize: 20, color: 'white' }} />
              )}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" sx={{ color: formData.productType === 'auction' ? '#f59e0b' : '#667eea' }}>
                {formData.productType === 'auction' ? t('productUpload.productType.auction') : t('productUpload.productType.direct')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formData.productType === 'auction' 
                  ? t('productUpload.productType.auction.desc') 
                  : t('productUpload.productType.direct.desc')
                }
              </Typography>
            </Box>
          </Box>
        </Card>

        {/* Main Product Summary */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
            {t('productUpload.review.productSummary')}
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Left Column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.name')}</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ textAlign: 'right', maxWidth: 200 }}>
                  {formData.name}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.category')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedCategory?.name || 'Not selected'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.subcategory')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {selectedSubcategory?.name || 'Not selected'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {formData.productType === 'auction' ? t('productUpload.labels.minBidPrice') : t('productUpload.labels.price')}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
                  ${formData.price}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.stock')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formData.stock} {t('productUpload.labels.units')}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.brand')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formData.brand}
                </Typography>
              </Box>
            </Box>

            {/* Right Column */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.sizeSystem')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {sizeSystem?.name || 'Not selected'}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.images')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formData.images.length} images
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.material')}</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {formData.material}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">{t('productUpload.labels.condition')}</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                  {formData.condition}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Auction-specific information */}
        {formData.productType === 'auction' && (
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2, border: '2px solid #f59e0b', bgcolor: '#fef3c7' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: '#92400e', display: 'flex', alignItems: 'center', gap: 1 }}>
              <AuctionIcon sx={{ fontSize: 20 }} />
              {t('productUpload.review.auctionDetails')}
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.startPrice')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    ${formData.price || 'Not set'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.bidIncrement')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    ${formData.auctionBidIncrement || 'Not set'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.end')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    {formData.auctionEndTime ? new Date(formData.auctionEndTime).toLocaleString() : t('productUpload.labels.notSet')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.reserve')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    {formData.auctionReservePrice ? `$${formData.auctionReservePrice}` : t('productUpload.labels.noReserve')}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.duration')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    {formData.auctionEndTime ? 
                      `${Math.ceil((new Date(formData.auctionEndTime).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))} ${t('productUpload.labels.days')}` : 
                      t('productUpload.labels.notSet')
                    }
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('productUpload.auction.status')}</Typography>
                  <Typography variant="body2" fontWeight="bold" sx={{ color: '#f59e0b' }}>
                    {formData.auctionEndTime && new Date(formData.auctionEndTime) > new Date() ? t('productUpload.labels.active') : t('productUpload.labels.ended')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Card>
        )}

        {/* Boost & Visibility Summary */}
        <Card
          sx={(t) => ({
            p: 3,
            borderRadius: 2,
            boxShadow: 1,
            border: '2px solid',
            borderColor: boostTotal > 0 ? t.palette.success.light : t.palette.divider,
            background: boostTotal > 0
              ? `linear-gradient(135deg, ${t.palette.success.main}14 0%, ${t.palette.primary.main}14 100%)`
              : t.palette.background.paper,
          })}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Boost & Visibility
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Selected options and total cost for publishing.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">Total</Typography>
              <Typography variant="h6" fontWeight={900} sx={{ color: boostTotal > 0 ? 'success.main' : 'text.primary' }}>
                {boostTotal.toFixed(2)} {currency}
              </Typography>
            </Box>
          </Box>

          {selectedBoost.length === 0 ? (
            <Alert severity="info">
              No boost options selected. You can publish normally.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {selectedBoost.map((it) => (
                <Box
                  key={it.id}
                  sx={(t) => ({
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 2,
                    border: `1px solid ${t.palette.divider}`,
                    backgroundColor: 'rgba(255,255,255,0.65)',
                  })}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography fontWeight={900} sx={{ lineHeight: 1.2 }}>
                      {it.title || it.id}
                    </Typography>
                    {!!it.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                        {it.description}
                      </Typography>
                    )}
                  </Box>
                  <Typography fontWeight={900} sx={{ whiteSpace: 'nowrap' }}>
                    {(Number(it.price) || 0).toFixed(2)} {currency}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Card>

        {/* Colors Section */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            {t('productUpload.review.selectedColors')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.colors.map((color, index) => (
              <Chip
                key={index}
                label={color}
                sx={{
                  backgroundColor: color.toLowerCase(),
                  color: 'white',
                  fontWeight: 'bold',
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  '& .MuiChip-label': {
                    color: 'white',
                    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                  }
                }}
              />
            ))}
            {formData.customColors.map((color, index) => (
              <Chip
                key={`custom-${index}`}
                label={color}
                variant="outlined"
                sx={{
                  borderColor: color.toLowerCase(),
                  color: color.toLowerCase(),
                  fontWeight: 'bold',
                }}
              />
            ))}
            {formData.colors.length === 0 && formData.customColors.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('productUpload.review.noColors')}
              </Typography>
            )}
          </Box>
        </Card>

        {/* Sizes Section */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            {t('productUpload.review.selectedSizes')}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {formData.sizes.map((size, index) => (
              <Chip
                key={index}
                label={size}
                variant="outlined"
                sx={{
                  fontWeight: 'bold',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                }}
              />
            ))}
            {formData.sizes.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                {t('productUpload.review.noSizes')}
              </Typography>
            )}
          </Box>
        </Card>

        {/* Shipping Options Section */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            {t('productUpload.review.selectedShipping')}
          </Typography>
          {selectedDeliveryOptions.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedDeliveryOptions.map((option) => (
                <Box key={option.id} sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'grey.300', 
                  borderRadius: 1,
                  backgroundColor: 'grey.50'
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight="bold">
                      {option.name}
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: 'primary.main' }}>
                      €{option.price_eur || 0}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('productUpload.labels.carrier')} {option.carrier} • {t('productUpload.labels.country')} {option.country}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {t('productUpload.labels.maxWeight')} {option.max_weight_kg ? `${option.max_weight_kg}kg` : t('productUpload.labels.notSet')} • 
                    {t('productUpload.labels.tracking')} {option.tracking ? t('productUpload.labels.yes') : t('productUpload.labels.no')}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('productUpload.review.noShipping')}
            </Typography>
          )}
        </Card>

        {/* Location Section */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
            {t('productUpload.review.selectedLocation')}
          </Typography>
          {selectedAddressId ? (
            (() => {
              const selectedAddress = addresses.find(addr => addr.id === selectedAddressId);
              return selectedAddress ? (
                <Box sx={{ 
                  p: 2, 
                  border: '1px solid', 
                  borderColor: 'grey.300', 
                  borderRadius: 1,
                  backgroundColor: 'grey.50'
                }}>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedAddress.title}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedAddress.full_name}
                  </Typography>
                  {selectedAddress.company && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedAddress.company}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {selectedAddress.address_line_1}
                  </Typography>
                  {selectedAddress.address_line_2 && (
                    <Typography variant="body2">
                      {selectedAddress.address_line_2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {selectedAddress.postal_code} {selectedAddress.city}
                  </Typography>
                  <Typography variant="body2">
                    {selectedAddress.state}, {selectedAddress.country}
                  </Typography>
                  {selectedAddress.phone && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      📞 {selectedAddress.phone}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('productUpload.review.addressNotFound')}
                </Typography>
              );
            })()
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {t('productUpload.review.noLocation')}
            </Typography>
          )}
        </Card>

        {/* Tags Section */}
        {formData.tags.length > 0 && (
          <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
              {t('productUpload.review.productTags')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  sx={{
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                    fontWeight: 'bold',
                  }}
                />
              ))}
            </Box>
          </Card>
        )}

        {/* Publish Toggle */}
        <Card sx={{ p: 3, borderRadius: 2, boxShadow: 1 }}>
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch 
                    {...field} 
                    checked={field.value}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#667eea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      {t('productUpload.review.publishNow')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('productUpload.review.publishNow.desc')}
                    </Typography>
                  </Box>
                }
              />
            )}
          />
        </Card>
      </Box>
    );
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return renderProductType();
      case 1:
        return renderBasicInfo();
      case 2:
        return renderDetails();
      case 3:
        return renderImages();
      case 4:
        return renderShipping();
      case 5:
        return renderLocation();
      case 6:
        return renderBoostAndVisibility();
      case 7:
        return renderReview();
      default:
        return t('productUpload.labels.unknownStep');
    }
  };

  const StepPulseIcon = (props: { active?: boolean; completed?: boolean; icon?: React.ReactNode; pulse?: boolean; softPulse?: boolean }) => {
    const { active, completed, icon, pulse, softPulse } = props;
    const outer = isMobile ? 28 : 32;
    const inner = isMobile ? 22 : 26;
    const badge = isMobile ? 14 : 16;
    return (
      <Box
        sx={{
          position: 'relative',
          width: outer,
          height: outer,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          '@keyframes stepPulse': {
            '0%': { transform: 'scale(0.88)', opacity: 0.55 },
            '70%': { transform: 'scale(1.34)', opacity: 0 },
            '100%': { transform: 'scale(1.34)', opacity: 0 },
          },
          '@keyframes stepPulseSoft': {
            '0%': { transform: 'scale(0.92)', opacity: 0.35 },
            '70%': { transform: 'scale(1.26)', opacity: 0 },
            '100%': { transform: 'scale(1.26)', opacity: 0 },
          },
        }}
      >
        {(pulse || softPulse) && (
          <Box
            sx={{
              position: 'absolute',
              inset: -6,
              borderRadius: '50%',
              border: pulse ? '2px solid rgba(255,255,255,0.85)' : '2px solid rgba(255,255,255,0.55)',
              boxShadow: pulse ? '0 10px 26px rgba(0,0,0,0.18)' : '0 8px 22px rgba(0,0,0,0.12)',
              animation: pulse ? 'stepPulse 1.35s ease-in-out infinite' : 'stepPulseSoft 1.8s ease-in-out infinite',
              pointerEvents: 'none',
            }}
          />
        )}
        <Box
          sx={{
            width: inner,
            height: inner,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: active
              ? 'rgba(255,255,255,0.98)'
              : completed
                ? 'rgba(255,255,255,0.85)'
                : 'rgba(255,255,255,0.62)',
            border: active ? '2px solid rgba(255,255,255,0.95)' : '1px solid rgba(255,255,255,0.55)',
            color: active ? '#9a3412' : completed ? 'rgba(0,0,0,0.70)' : 'rgba(255,255,255,0.85)',
            boxShadow: active ? '0 10px 24px rgba(0,0,0,0.18)' : completed ? '0 8px 18px rgba(0,0,0,0.12)' : 'none',
            backdropFilter: 'blur(6px)',
            transition: 'all 0.18s ease',
          }}
        >
          <Typography sx={{ fontWeight: 900, fontSize: isMobile ? '0.8rem' : '0.85rem', lineHeight: 1 }}>
            {icon}
          </Typography>
        </Box>

        {softPulse && !pulse && (
          <Box
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: badge,
              height: badge,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.95)',
              border: '1px solid rgba(0,0,0,0.12)',
              boxShadow: '0 8px 18px rgba(0,0,0,0.16)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
            aria-hidden
          >
            <EditIcon sx={{ fontSize: isMobile ? 11 : 12, color: '#7c2d12' }} />
          </Box>
        )}
      </Box>
    );
  };

  const UploadTimeline = () => {
    const allowedMax = Math.max(
      lastEditedStep,
      activeStep,
      ...(Array.isArray(completedSteps) && completedSteps.length > 0 ? completedSteps : [0])
    );

    return (
      <Box
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
          mx: { xs: -2, sm: 0 },
          px: { xs: 2, sm: 0 },
          pb: 0.5,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: { xs: 1.25, sm: 0 },
            minWidth: { xs: steps.length * 116, sm: '100%' },
            pr: { xs: 1, sm: 0 },
            scrollSnapType: { xs: 'x mandatory', sm: 'none' },
          }}
        >
          {steps.map((label, index) => {
            const disabled = index > allowedMax;
            const active = index === activeStep;
            const completed = completedSteps.includes(index);
            const softPulse = index === lastEditedStep && index !== activeStep;
            const outer = isMobile ? 28 : 32;

            return (
              <Box
                key={`${label}-${index}`}
                onClick={() => {
                  if (disabled) return;
                  handleStepClick(index);
                }}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  flex: { xs: '0 0 auto', sm: '1 0 0' },
                  minWidth: { xs: 106, sm: 0 },
                  px: { xs: 0.25, sm: 0.5 },
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.6 : 1,
                  scrollSnapAlign: { xs: 'start', sm: 'none' },
                  userSelect: 'none',
                }}
              >
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: outer / 2 + 4,
                      left: `calc(50% + ${outer / 2 + 10}px)`,
                      right: { xs: -34, sm: `calc(-50% + ${outer / 2 + 10}px)` },
                      height: 2,
                      borderRadius: 999,
                      bgcolor: completed
                        ? 'rgba(255,255,255,0.65)'
                        : 'rgba(255,255,255,0.32)',
                      zIndex: 0,
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    pt: 0.5,
                  }}
                >
                  <StepPulseIcon
                    active={active}
                    completed={completed}
                    icon={index + 1}
                    pulse={active}
                    softPulse={softPulse}
                  />

                  <Typography
                    sx={{
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.78rem', sm: '0.95rem' },
                      fontWeight: active ? 900 : completed ? 700 : 500,
                      color: active
                        ? 'rgba(255, 255, 255, 0.98)'
                        : softPulse
                          ? 'rgba(255, 255, 255, 0.92)'
                          : completed
                            ? 'rgba(255, 255, 255, 0.86)'
                            : 'rgba(255, 255, 255, 0.6)',
                      textShadow: active ? '0 8px 18px rgba(0,0,0,0.22)' : 'none',
                      transition: 'all 0.2s ease-in-out',
                      pb: 0.25,
                    }}
                  >
                    {label}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Box
        sx={(t) => ({
          background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          color: t.palette.primary.contrastText,
          py: { xs: 2.5, sm: 4 },
        })}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1.5, sm: 2 },
              mb: { xs: 2, sm: 3 },
            }}
          >
            <IconButton
              onClick={() => navigate('/dashboard')}
              size={isMobile ? 'small' : 'medium'}
              sx={(t) => ({ color: t.palette.primary.contrastText, ml: { xs: -1, sm: 0 } })}
            >
              <BackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h4"
                fontWeight="bold"
                gutterBottom
                sx={{
                  fontSize: { xs: '2.05rem', sm: '2.4rem' },
                  lineHeight: 1.1,
                  mb: { xs: 0.5, sm: 1 },
                }}
              >
                {t('productUpload.header.title')}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.92,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  lineHeight: 1.35,
                  maxWidth: { xs: 520, sm: 760 },
                }}
              >
                {(() => {
                  const raw = t('productUpload.header.subtitle');
                  if (!platformName) return raw;
                  return raw.replace(/FashionHub/gi, platformName);
                })()}
              </Typography>
            </Box>
          </Box>

          <UploadTimeline />
          {(() => {
            const labelRaw = t('productUpload.buttons.startFromScratch');
            const label = labelRaw && labelRaw !== 'productUpload.buttons.startFromScratch' ? labelRaw : 'Start from scratch';
            return (
          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: { xs: 1.25, sm: 2 } }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setConfirmResetOpen(true)}
              startIcon={<RotateLeftIcon />}
              disabled={isEnsuringWip}
              fullWidth={isMobile}
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.6)',
                borderRadius: 999,
                py: { xs: 1.05, sm: 0.8 },
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {label}
            </Button>
          </Box>
            );
          })()}
        </Container>
      </Box>

      <Dialog
        open={confirmResetOpen}
        onClose={() => setConfirmResetOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ p: 3, pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(239,68,68,0.06) 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444'
            }}>
              <RotateLeftIcon />
            </Box>
            <Typography variant="h6" fontWeight={800}>
              {tx('productUpload.reset.confirmTitle', 'Start from scratch?')}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {tx('productUpload.reset.confirmDesc', 'This will clear the form and reset this draft. Your current inputs on this page will be lost. You can’t undo this action.')}
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setConfirmResetOpen(false)} sx={{ textTransform: 'none' }}>
            {tx('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setConfirmResetOpen(false);
              await handleStartFromScratch();
            }}
            sx={{ textTransform: 'none' }}
          >
            {tx('productUpload.reset.confirmYes', 'Yes, reset')}
          </Button>
        </Box>
      </Dialog>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <motion.div
              key={activeStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              {getStepContent(activeStep)}
            </motion.div>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<BackIcon />}
              >
                {t('productUpload.nav.back')}
              </Button>

              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit(handlePublishClick)}
                  disabled={isUploading || paypalProcessing}
                  startIcon={isUploading ? <CircularProgress size={22} color="inherit" /> : <SparkleIcon />}
                  sx={(t) => ({
                    px: { xs: 3.75, sm: 5 },
                    py: { xs: 1.25, sm: 1.35 },
                    fontSize: { xs: '1.02rem', sm: '1.08rem' },
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 900,
                    background: `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.secondary.main} 100%)`,
                    boxShadow: `0 10px 24px ${t.palette.primary.main}33`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.secondary.dark} 100%)`,
                      transform: 'translateY(-1px)',
                      boxShadow: `0 14px 32px ${t.palette.primary.main}44`,
                    },
                    '&:active': {
                      transform: 'translateY(0px) scale(0.99)',
                    },
                    transition: 'all 0.18s ease',
                  })}
                >
                  {isUploading ? t('productUpload.buttons.publishing') : t('productUpload.buttons.publish')}
                </Button>
              ) : (
                <Button
                  size="large"
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<PreviewIcon />}
                  disabled={isUploading || (activeStep === 1 && !hasSelectedCategory)}
                  sx={(t) => ({
                    px: 3.5,
                    py: 1.1,
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    borderRadius: 3,
                    backgroundColor: (activeStep === 1 && !hasSelectedCategory)
                      ? t.palette.primary.main
                      : (hasMapLocationNotAdded ? t.palette.warning.main : t.palette.primary.main),
                    '&:hover': {
                      backgroundColor: (activeStep === 1 && !hasSelectedCategory)
                        ? t.palette.primary.dark
                        : (hasMapLocationNotAdded ? t.palette.warning.dark : t.palette.primary.dark),
                    },
                    ...(((activeStep === 1 && !hasSelectedCategory) || hasMapLocationNotAdded) && {
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { boxShadow: `0 0 0 0 ${t.palette.primary.main}40` },
                        '70%': { boxShadow: `0 0 0 12px ${t.palette.primary.main}00` },
                        '100%': { boxShadow: `0 0 0 0 ${t.palette.primary.main}00` },
                      },
                    }),
                  })}
                >
                  {(activeStep === 1 && !hasSelectedCategory)
                    ? tx('productUpload.nav.chooseCategoryFirst', 'Choose a category to continue')
                    : (hasMapLocationNotAdded ? t('productUpload.nav.addAddressFirst') : t('productUpload.nav.next'))}
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Image Cropping Modal */}
      <Dialog
        open={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            maxHeight: isMobile ? '100vh' : '90vh',
            height: 'auto',
            borderRadius: isMobile ? 0 : 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CropIcon />
            <Typography variant="h6">{t('productUpload.crop.title')}</Typography>
          </Box>
          <IconButton onClick={() => setCropModalOpen(false)}>
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
          <Box sx={{ position: 'relative', minHeight: { xs: 'calc(100vh - 180px)', sm: 400 }, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 1, sm: 2 } }}>
            {cropImage && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={undefined} // Free aspect ratio
                minWidth={100}
                minHeight={100}
              >
                <img
                  alt="Crop me"
                  src={cropImage}
                  style={{
                    transform: `scale(${scale}) rotate(${rotate}deg)`,
                    maxWidth: '100%',
                    maxHeight: isMobile ? 'calc(100vh - 220px)' : '70vh',
                    objectFit: 'contain',
                  }}
                  onLoad={handleImageLoad}
                />
              </ReactCrop>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 1.5, sm: 2 }, 
          gap: 1,
          flexWrap: 'wrap',
          position: isMobile ? 'sticky' : 'static',
          bottom: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', gap: 1, flex: 1, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setScale(Math.max(0.5, scale - 0.1))}
              startIcon={<ZoomOutIcon />}
            >
              {t('productUpload.crop.zoomOut')}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setScale(Math.min(3, scale + 0.1))}
              startIcon={<ZoomInIcon />}
            >
              {t('productUpload.crop.zoomIn')}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setRotate(rotate - 90)}
              startIcon={<RotateLeftIcon />}
            >
              {t('productUpload.crop.rotateLeft')}
            </Button>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => setRotate(rotate + 90)}
              startIcon={<RotateRightIcon />}
            >
              {t('productUpload.crop.rotateRight')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Button
              onClick={() => setCropModalOpen(false)}
              variant="outlined"
              startIcon={<CancelIcon />}
              fullWidth={isMobile}
            >
              {t('productUpload.crop.cancel')}
            </Button>
            <Button
              onClick={applyCrop}
              variant="contained"
              startIcon={isUploadingImage ? <CircularProgress size={20} color="inherit" /> : <CropIcon />}
              disabled={!completedCrop || isUploadingImage}
              fullWidth={isMobile}
            >
              {isUploadingImage ? t('productUpload.crop.uploading') : t('productUpload.crop.apply')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Camera Capture Modal */}
      <Dialog
        open={cameraModalOpen}
        onClose={() => {
          stopCamera();
          setCameraModalOpen(false);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: 'auto',
            bgcolor: '#000',
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          bgcolor: '#000',
          color: 'white',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CameraIcon />
            <Typography variant="h6">{t('productUpload.camera.title')}</Typography>
          </Box>
          <IconButton
            onClick={() => {
              stopCamera();
              setCameraModalOpen(false);
            }}
            sx={{ color: 'white' }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: '#000', position: 'relative' }}>
          <Box sx={{ 
            position: 'relative',
            width: '100%',
            minHeight: 400,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#000',
          }}>
            {videoStream ? (
              <video
                ref={setVideoRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                }}
              />
            ) : (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                gap: 2,
                py: 8,
                color: 'white'
              }}>
                <CircularProgress sx={{ color: 'white' }} />
                <Typography>{t('productUpload.camera.starting')}</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 2, 
          gap: 1, 
          bgcolor: '#000',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={switchCamera}
            variant="outlined"
            startIcon={<FlipCameraIcon />}
            sx={{ 
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            disabled={!videoStream}
          >
            {t('productUpload.camera.switch')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => {
                stopCamera();
                setCameraModalOpen(false);
              }}
              variant="outlined"
              sx={{ 
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {t('productUpload.camera.cancel')}
            </Button>
            <Button
              onClick={capturePhoto}
              variant="contained"
              startIcon={<CameraIcon />}
              disabled={!videoStream || !videoRef}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark'
                },
                minWidth: 140
              }}
            >
              {t('productUpload.camera.capture')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Image Gallery */}
      <ImageGallery
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        images={watchedImages || []}
        currentIndex={galleryIndex}
        onIndexChange={handleGalleryIndexChange}
        title={t('productUpload.gallery.title')}
        showDownload={true}
        showRotation={true}
        showNavigation={true}
        showZoom={true}
        showFullscreen={true}
      />

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f8fffe 0%, #f0fdf4 100%)',
            border: '1px solid #10b981',
            boxShadow: '0 25px 50px -12px rgba(16, 185, 129, 0.25)',
          },
        }}
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 15,
              delay: 0.1 
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ 
                  type: "spring", 
                  stiffness: 200, 
                  damping: 15,
                  delay: 0.3 
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 40, color: 'white' }} />
              </motion.div>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 'bold', 
                color: '#065f46', 
                mb: 2,
                background: 'linear-gradient(135deg, #065f46 0%, #10b981 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t('productUpload.success.title')}
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#6b7280', 
                mb: 3,
                fontSize: '1.1rem',
              }}
            >
              {t('productUpload.success.descriptionPrefix')} "{uploadedProduct?.name}" {t('productUpload.success.descriptionSuffix')}
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() => setShowSuccessModal(false)}
                sx={{
                  borderColor: '#10b981',
                  color: '#10b981',
                  '&:hover': {
                    borderColor: '#059669',
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  },
                }}
              >
                {t('productUpload.success.close')}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate('/dashboard');
                }}
                sx={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  },
                }}
              >
                {t('productUpload.success.viewDashboard')}
              </Button>
            </Box>
          </motion.div>
        </DialogContent>
      </Dialog>

      {/* Documents Modal */}
      <UserDocumentsModal
        open={documentsModalOpen}
        onClose={() => setDocumentsModalOpen(false)}
        onDocumentSelect={handleDocumentSelect}
        mode="select"
        imagesOnly={true}
      />

      {/* Map Popup */}
      <MapSearchNew
        open={mapPopupOpen}
        onClose={() => setMapPopupOpen(false)}
        onLocationSelect={handleLocationSelect}
        selectedLocations={selectedLocations}
        initialSearchQuery={selectedAddressForMap ? 
          `${selectedAddressForMap.address_line_1}, ${selectedAddressForMap.postal_code} ${selectedAddressForMap.city}, ${selectedAddressForMap.country}` 
          : selectedMapLocation ? 
            (() => {
              // Construct a proper search query from the map location data
              const parts = [];
              if (selectedMapLocation.street) parts.push(selectedMapLocation.street);
              if (selectedMapLocation.city) parts.push(selectedMapLocation.city);
              if (selectedMapLocation.state) parts.push(selectedMapLocation.state);
              if (selectedMapLocation.country) parts.push(selectedMapLocation.country);
              return parts.join(', ');
            })()
            : undefined
        }
        addressMode={!!selectedAddressForMap}
        addressData={selectedAddressForMap ? {
          title: selectedAddressForMap.title,
          full_name: selectedAddressForMap.full_name,
          company: selectedAddressForMap.company,
          address_line_1: selectedAddressForMap.address_line_1,
          address_line_2: selectedAddressForMap.address_line_2,
          city: selectedAddressForMap.city,
          state: selectedAddressForMap.state,
          postal_code: selectedAddressForMap.postal_code,
          country: selectedAddressForMap.country,
          phone: selectedAddressForMap.phone,
        } : undefined}
        initialCoordinates={selectedMapLocation && (selectedMapLocation.lat !== 0 || selectedMapLocation.lng !== 0) ? {
          lat: selectedMapLocation.lat,
          lng: selectedMapLocation.lng
        } : undefined}
      />

      {/* Settings Modal */}
      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialTab={3} // Addresses tab
        onAddressAdded={handleAddressAdded}
        readonlyFields={!!selectedMapLocation} // Make fields readonly when opened from map location
        coordinates={selectedMapLocation ? { lat: selectedMapLocation.lat, lng: selectedMapLocation.lng } : undefined}
        prefilledAddress={selectedMapLocation ? {
          title: (() => {
            // Generate a more descriptive title
            if (selectedMapLocation.street && selectedMapLocation.houseNumber) {
              return `${selectedMapLocation.houseNumber} ${selectedMapLocation.street}`;
            } else if (selectedMapLocation.street) {
              return selectedMapLocation.street;
            } else if (selectedMapLocation.city) {
              return selectedMapLocation.city;
            }
            return 'Map Location';
          })(),
          full_name: '',
          company: '',
          address_line_1: (() => {
            // Combine house number and street name properly
            const parts = [];
            if (selectedMapLocation.houseNumber) {
              parts.push(selectedMapLocation.houseNumber);
            }
            if (selectedMapLocation.street) {
              parts.push(selectedMapLocation.street);
            }
            // If we have no parts, try to extract from the full name
            if (parts.length === 0 && selectedMapLocation.name) {
              // Try to extract the first part of the address from the full name
              const nameParts = selectedMapLocation.name.split(',');
              if (nameParts.length > 0) {
                return nameParts[0].trim();
              }
            }
            return parts.join(' ');
          })(),
          address_line_2: '',
          city: selectedMapLocation.city || '',
          state: selectedMapLocation.state || '',
          postal_code: selectedMapLocation.postalCode || '',
          country: selectedMapLocation.country || '',
          phone: '',
          is_default: false,
        } : undefined}
      />
    </Box>
  );
};

export default ProductUploadPage;
